import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  // Chip rimosso: non mostra più il negoziod temporaneo nella vista desktop
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CheckCircle, Save, Cancel, Schedule, AccessTime, Add, Remove, /* ChangeCircle, ChangeCircleOutlined, */ FileDownload, Favorite, FavoriteBorder } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import { apiRequest } from '../api/api';
import { useTurniDipendenteByCodice, useStatsDipendente, useFestivita } from '../api/queries';
import InlineLoader from './common/InlineLoader';
import { preferenzeService, cambiTurnoService, bloccoService, impostazioniGlobaliService, orariGeneratiService } from '../api/services';
import CambiTurnoPopup from './CambiTurnoPopup';
import { CambiTurnoStats, Dipendente } from '../types/types';
import PreferenceLimitDialog from './PreferenceLimitDialog';
import { isCompetenzaLocked, LOCK_MESSAGE } from '../utils/competenzaLock';
import { getApiErrorMessage } from '../utils/apiError';

// Configura dayjs per utilizzare la localizzazione italiana
dayjs.locale('it');

// Colore tema per Il Mio Orario
const PAGE_COLOR = '#2196F3';

// Opzioni per il tipo orario
const TIPO_ORARIO_OPTIONS = [
  'LAVORATO',
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'RIPOSO',
  'ROL',
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'SMART WORKING',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO'
];

// Opzioni ristrette per il dipendente (di base)
const TIPO_ORARIO_OPTIONS_RESTRICTED = [
  'LAVORATO',
  'ROL'
];

// Elenco tipi orario che devono impostare Turno = Assenza
const TIPO_ASSENZA_LIST = [
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'ROL',
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANAZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO'
];

// Categoria A: preimpostazione orari (assenze a giornata intera, non permettono consuntivo)
const TIPO_ASSENZA_PREIMPOSTA_ORARI = [
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'CONGEDO MATRIMONIALE',
  'ASPETTATIVA'
];

// Codici che disabilitano completamente i campi consuntivo (non precompilati e non compilabili)
const CODICI_DISABILITA_CONSUNTIVO = [
  'ASSENZA NON RETRIBUITA'
];

// Assenze che permettono di inserire orari consuntivo (assenze parziali/permessi orari)
// CORSI AI DIPENDENTI e SMART WORKING: hanno orari previsti impostati dall'utente, permettono consolidamento
const ASSENZE_CON_CONSUNTIVO = [
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING'
];

// Codici tipo orario che richiedono calcolo automatico (disabilitano i campi orario) = Categoria A
// CHIUSO DITTA rimosso perché ora mostra le ore calcolate in base al contratto
// ASSENZA NON RETRIBUITA aggiunto per disabilitare completamente i campi consuntivo
const CODICI_CALCOLO_AUTOMATICO = [...TIPO_ASSENZA_PREIMPOSTA_ORARI, 'ROL', ...CODICI_DISABILITA_CONSUNTIVO];

const isAssenzaTipo = (t?: string | null) => !!t && TIPO_ASSENZA_LIST.includes(t);
const isCategoriaA = (t?: string | null) => !!t && TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(t);
// Categoria B: assenze che richiedono il consuntivo; ASSENZA NON RETRIBUITA usa solo il previsto.
const isCategoriaB = (t?: string | null) => isAssenzaTipo(t)
  && !isCategoriaA(t)
  && t !== 'LEGGE 104 PERMESSO'
  && t !== 'ASSENZA NON RETRIBUITA';

// Funzione helper per verificare se un'assenza permette di inserire orari consuntivo
const isAssenzaConConsuntivo = (codiceTipoOrario: string | null | undefined): boolean => {
  return codiceTipoOrario ? ASSENZE_CON_CONSUNTIVO.includes(codiceTipoOrario) : false;
};

// Funzione helper per verificare se un tipo orario deve inviare gli orari consuntivi al backend
// Solo ROL invia orari consuntivi espliciti. LEGGE 104 PERMESSO usa solo orari previsionali.
const shouldSendConsuntivoTimes = (tipoOrario: string, oraConsInizio: string, oraConsFine: string): boolean => {
  return (tipoOrario === 'ROL') && !!oraConsInizio && !!oraConsFine;
};

// Verifica se il dipendente può modificare/eliminare un turno in base al tipo orario
// Il dipendente può modificare solo turni con tipi che può inserire autonomamente
const canDipendenteModifyTipoOrario = (
  tipoOrario: string | null | undefined,
  permessiSpeciali: { hasSpecial: boolean; remainingGg?: number; tipo?: number } | null
): boolean => {
  if (!tipoOrario) return true; // Se non c'è tipo orario, può modificare
  // Tipi base sempre modificabili dal dipendente
  const allowedTypes = [...TIPO_ORARIO_OPTIONS_RESTRICTED]; // LAVORATO, ROL
  // Se ha permessi speciali tipo 1 (Legge 104) con giorni disponibili
  if (permessiSpeciali?.hasSpecial && permessiSpeciali.tipo === 1 && (permessiSpeciali.remainingGg ?? 0) > 0) {
    allowedTypes.push('LEGGE 104 PERMESSO');
  }
  return allowedTypes.includes(tipoOrario);
};

const isPreferenceTurno = (orario?: { preferenza?: boolean | number | null }): boolean => {
  return Number(orario?.preferenza || 0) === 1;
};

const getPreferencePayload = (response: any) => response?.data ?? response?.turno ?? response;

// Helper: verifica se è un record RIPOSO senza turno (turno null/vuoto con codiceTipoOrario = 'RIPOSO')
// Questi record devono apparire come "Disponibile" e permettere l'inserimento di preferenze
const isRiposoSenzaTurno = (orario: { turno?: string | null; codiceTipoOrario?: string | null }): boolean => {
  const isTurnoEmpty = !orario.turno || orario.turno.trim() === '' || orario.turno === 'turnoVuoto';
  const isRiposo = orario.codiceTipoOrario === 'RIPOSO';
  return isTurnoEmpty && isRiposo;
};

// Helper: verifica se è un giorno di Riposo (con turno assegnato o senza turno)
// Questi record devono permettere l'inserimento di preferenze con cuoricino
const isRiposoDay = (orario: { turno?: string | null; codiceTipoOrario?: string | null }): boolean => {
  return orario.codiceTipoOrario === 'RIPOSO' || 
         (orario.turno || '').toLowerCase() === 'riposo';
};

// Ruoli che devono confermare gli orari in ordine cronologico
const RUOLI_CON_RESTRIZIONE_CONFERMA = ['dipendente', 'linker', 'maker', 'referente'];

// Verifica se il ruolo richiede la conferma sequenziale dei turni LAVORATO
const isRuoloConRestrizioneConferma = (ruolo: string): boolean => {
  return RUOLI_CON_RESTRIZIONE_CONFERMA.includes((ruolo || '').toLowerCase());
};

// Tipi orario che richiedono consolidamento obbligatorio (oltre a LAVORATO):
// Categoria 2 = assenze parziali con calcolo ore rimanenti
// CORSI AI DIPENDENTI e SMART WORKING: hanno orari previsti impostati manualmente, richiedono consolidamento
const CODICI_OBBLIGO_CONSOLIDAMENTO = [
  'LAVORATO',
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
];

// Verifica se esistono turni (LAVORATO o Categoria 2) precedenti non confermati nello stesso mese
// Ritorna true se ci sono turni precedenti non confermati (blocca l'inserimento)
const hasPreviousUnconfirmedLavorato = (
  orari: { id: number; data: string; oraConsInizio?: string | null; oraConsFine?: string | null; codiceTipoOrario?: string | null }[],
  currentTurnoData: string
): boolean => {
  const currentDate = dayjs(currentTurnoData);
  const currentMonth = currentDate.format('YYYY-MM');

  // Filtra i turni con obbligo di consolidamento dello stesso mese con data precedente
  const previousTurni = orari.filter(turno => {
    const turnoDate = dayjs(turno.data);
    const turnoMonth = turnoDate.format('YYYY-MM');

    return turnoMonth === currentMonth &&
           turnoDate.isBefore(currentDate, 'day') &&
           CODICI_OBBLIGO_CONSOLIDAMENTO.includes(turno.codiceTipoOrario || '') &&
           turno.id > 0; // Solo turni reali, non placeholder
  });

  // Verifica se almeno uno di questi turni non ha conferma (oraConsInizio o oraConsFine vuoti)
  return previousTurni.some(turno =>
    !turno.oraConsInizio || !turno.oraConsFine
  );
};

interface TurnoCreato {
  id: number;
  neg: string;
  descrizione: string;
  data: string;
  oraConsInizio: string | null;
  oraConsFine: string | null;
  oraPrevInizio: string | null;
  oraPrevFine: string | null;
  giornoSettimana: string;
  turno: string;
  // Codice tipo orario restituito dal backend
  codiceTipoOrario?: string;
  tipoOrario?: string;
  annotazioni?: string;
  utenteModifica: string;
  dataModifica: string;
  blocco?: boolean;
  bloccoAutomatico?: boolean;
  preferenza?: boolean | number;
  // Flag UI: giorno fuori dal mese selezionato
  outOfMonth?: boolean;
}

type Ruolo = 'Dipendente' | 'ResponsabileOrari' | 'Coordinatore' | 'Admin' | 'Linker' | 'Maker' | 'Referente';
interface MioOrarioProps {
  overrideUser?: { nome: string; ruolo: Ruolo; negozio?: string };
  showCambiTurno?: boolean;
  hideHeader?: boolean;
  initialMonth?: number; // 1-12
  initialYear?: number;  // e.g., 2025
  dataControlloOverride?: string | null;
  additionalCalendarContent?: React.ReactNode; // Contenuto aggiuntivo da mostrare sotto il calendario
  /** Se true, il cuoricino delle preferenze è sempre visibile indipendentemente dal periodo (per Admin/Coord) */
  alwaysShowPreferences?: boolean;
  onStatsChange?: (stats: {
    oreContratto: number;
    orePianificate: number;
    oreConsuntivate: number;
    assenzeContrattuali: number;
    attuale: number;
    differenzaAttuale: number;
  }) => void;
}

const MioOrario: React.FC<MioOrarioProps> = ({ overrideUser, showCambiTurno = true, hideHeader = false, initialMonth, initialYear, dataControlloOverride, additionalCalendarContent, alwaysShowPreferences = false, onStatsChange }) => {
  useEffect(() => {
    document.documentElement.dataset.turnoSource = 'MIO_ORARIO';
    return () => { delete document.documentElement.dataset.turnoSource; };
  }, []);

  const [orari, setOrari] = useState<TurnoCreato[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [editingRows, setEditingRows] = useState<{[key: number]: {oraConsInizio: string, oraConsFine: string, tipoOrario: string, annotazioni: string}}>({});
  const [showActionsForRow, setShowActionsForRow] = useState<number | null>(null);
  const [activeCell, setActiveCell] = useState<{rowId: number, field: 'oraConsInizio' | 'oraConsFine' | 'tipoOrario' | 'annotazioni'} | null>(null);
  // Statistiche dipendente
  const [dipendenteInfo, setDipendenteInfo] = useState<Dipendente | null>(null);
  const [stats, setStats] = useState({ oreContratto: 0, orePianificate: 0, oreConsuntivate: 0, assenzeContrattuali: 0, attuale: 0, differenzaAttuale: 0 });
  // Dipendenti autorizzati per LEGGE 104 PERMESSO (solo per Coordinatori)
  const [dipendentiAutorizzati104, setDipendentiAutorizzati104] = useState<string[]>([]);

  // Stati per gestione preferenze
  const [isPreferencesPeriod, setIsPreferencesPeriod] = useState(false);
  const [, setPreferenceCount] = useState(0);
  const [isTargetMonth, setIsTargetMonth] = useState(false);

  // Alias: true se il periodo è aperto OPPURE se Admin/Coord con alwaysShowPreferences
  const canShowPreferences = (isPreferencesPeriod && isTargetMonth) || alwaysShowPreferences;

  // Stati per dialog selezione turno
  const [turnoDialogOpen, setTurnoDialogOpen] = useState(false);
  const [selectedOrario, setSelectedOrario] = useState<TurnoCreato | null>(null);
  const [dialogType, setDialogType] = useState<'insert' | 'modify' | 'delete' | 'updateRiposo'>('insert');
  const [preferenceSubmitting, setPreferenceSubmitting] = useState(false);
  
  // Stato per mantenere la posizione dello scroll
  const [pendingScrollPosition, setPendingScrollPosition] = useState<number | null>(null);

  // Stati per cambi turno
  const [cambiTurnoPopupOpen, setCambiTurnoPopupOpen] = useState(false);
  const [, setCambiTurnoStats] = useState<CambiTurnoStats | null>(null);
  // Dialog limite preferenze
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogWeekPrefs, setLimitDialogWeekPrefs] = useState<{ id: number; data: string; turno?: string }[]>([]);
  
  // Stato per impostazione globale dal backend
  const [enableAlwaysConfirm, setEnableAlwaysConfirm] = useState(false);

  // Helper per ordinare sempre gli orari per data ascendente e per turno
  const sortOrariByDate = useCallback((list: TurnoCreato[]) => {
    // Ordina per data ascendente, e per lo stesso giorno ordina i turni
    // secondo l'ordine desiderato: Mattina, Intermezzo, Pomeriggio
    const turnoOrder: Record<string, number> = {
      'mattina': 0,
      'mattina p': 1,
      'intermezzo': 2,
      'intermezzo p': 3,
      'pomeriggio': 4,
      'pomeriggio p': 5,
      'riposo': 6,
      'disponibile': 7
    };

    const normalizeTurno = (raw: string) =>
      raw
        .toLowerCase()
        .replace(/\./g, '') // rimuovi punti es. "P."
        .replace(/\s+/g, ' ') // normalizza spazi
        .trim();

    return [...list].sort((a, b) => {
      const da = dayjs(a.data).valueOf();
      const db = dayjs(b.data).valueOf();
      if (da !== db) return da - db;

      // Stesso giorno: ordina per campo 'turno' (se presente), altrimenti per oraConsInizio
      const ta = normalizeTurno((a.turno || a.codiceTipoOrario || '').toString());
      const tb = normalizeTurno((b.turno || b.codiceTipoOrario || '').toString());

      const pa = Object.prototype.hasOwnProperty.call(turnoOrder, ta) ? turnoOrder[ta] : Number.MAX_SAFE_INTEGER;
      const pb = Object.prototype.hasOwnProperty.call(turnoOrder, tb) ? turnoOrder[tb] : Number.MAX_SAFE_INTEGER;

      if (pa !== pb) return pa - pb;

      // Se entrambi non sono riconosciuti o hanno lo stesso tipo, fallback ad ordinare per ora inizio consuntivo/previsto
      const timeA = (a.oraConsInizio || a.oraPrevInizio || '').toString();
      const timeB = (b.oraConsInizio || b.oraPrevInizio || '').toString();
      if (timeA && timeB) {
        return timeA.localeCompare(timeB);
      }

      return 0;
    });
  }, []);

  // Helper per export CSV: differenza HH:mm in ore
  const hhmmDiffToHours = (start?: string | null, end?: string | null) => {
    if (!start || !end) return 0;
    const s = dayjs(`2000-01-01 ${start}`);
    let e = dayjs(`2000-01-01 ${end}`);
    if (e.isBefore(s)) e = e.add(1, 'day');
    return e.diff(s, 'minute')/60;
  };

  // Utente visualizzato (può essere override) e utente loggato (viewer)
  const viewerInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  // Manteniamo compatibilità con il resto del componente che usa userInfo come "utente effettivo"
  const userInfo = overrideUser ?? viewerInfo;
  const isAdminViewer = (viewerInfo?.ruolo === 'Admin');
  const isCoordinatoreViewer = (viewerInfo?.ruolo === 'Coordinatore');
  const isAbilitaInsOrariViewer = !!(viewerInfo as any)?.abilitaInsOrari;
  const isActingForAnother = !!overrideUser && overrideUser.nome !== viewerInfo?.nome;
  // Codice personale del dipendente visualizzato (oldId viene usato come alias)
  const viewedCodicePersonale = (overrideUser as any)?.oldId || userInfo.oldId || '';
  
  // Helper per verificare se il dipendente visualizzato è autorizzato per LEGGE 104 PERMESSO
  const isDipendenteAutorizzato104 = useMemo(() => {
    // Admin: sempre autorizzato
    if (isAdminViewer) return true;
    // Lista vuota dopo il caricamento (nessun dipendente con permessi speciali tipo=1): nessuno è autorizzato
    // Nota: per l'Admin il backend restituisce lista vuota = tutti autorizzati (gestito sopra)
    // Per Coordinatore/abilitaInsOrari, lista vuota = nessun dipendente ha permessi speciali
    // Coordinatore o abilitaInsOrari: solo se il codice personale è nella lista
    if ((isCoordinatoreViewer || isAbilitaInsOrariViewer) && viewedCodicePersonale) {
      return dipendentiAutorizzati104.includes(viewedCodicePersonale);
    }
    return false;
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, viewedCodicePersonale, dipendentiAutorizzati104]);
  
  // Filtra i tipi orario in base al ruolo e autorizzazione del dipendente
  const TIPO_ORARIO_OPTIONS_FILTERED = useMemo(() => {
    if (isAdminViewer) return TIPO_ORARIO_OPTIONS;
    if ((isCoordinatoreViewer || isAbilitaInsOrariViewer) && isDipendenteAutorizzato104) return TIPO_ORARIO_OPTIONS;
    return TIPO_ORARIO_OPTIONS.filter(opt => opt !== 'LEGGE 104 PERMESSO');
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, isDipendenteAutorizzato104]);
  
  const TIPO_ASSENZA_LIST_FILTERED = useMemo(() => {
    if (isAdminViewer) return TIPO_ASSENZA_LIST;
    if ((isCoordinatoreViewer || isAbilitaInsOrariViewer) && isDipendenteAutorizzato104) return TIPO_ASSENZA_LIST;
    return TIPO_ASSENZA_LIST.filter(opt => opt !== 'LEGGE 104 PERMESSO');
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, isDipendenteAutorizzato104]);
  
  const ASSENZE_CON_CONSUNTIVO_FILTERED = useMemo(() => {
    if (isAdminViewer) return ASSENZE_CON_CONSUNTIVO;
    if ((isCoordinatoreViewer || isAbilitaInsOrariViewer) && isDipendenteAutorizzato104) return ASSENZE_CON_CONSUNTIVO;
    return ASSENZE_CON_CONSUNTIVO.filter(opt => opt !== 'LEGGE 104 PERMESSO');
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, isDipendenteAutorizzato104]);
  
  // Funzioni helper filtrate in base al ruolo
  const isAssenzaTipoFiltered = useCallback((t?: string | null) => {
    return !!t && TIPO_ASSENZA_LIST_FILTERED.includes(t);
  }, [TIPO_ASSENZA_LIST_FILTERED]);
  
  const isAssenzaConConsuntivoFiltered = useCallback((codiceTipoOrario: string | null | undefined): boolean => {
    return codiceTipoOrario ? ASSENZE_CON_CONSUNTIVO_FILTERED.includes(codiceTipoOrario) : false;
  }, [ASSENZE_CON_CONSUNTIVO_FILTERED]);
  
  const canDipendenteModifyTipoOrarioFiltered = useCallback((
    tipoOrario: string | null | undefined,
    permessiSpeciali: { hasSpecial: boolean; remainingGg?: number; tipo?: number } | null
  ): boolean => {
    if (!tipoOrario) return true;
    const allowedTypes = [...TIPO_ORARIO_OPTIONS_RESTRICTED];
    // Admin, Coordinatore e abilitaInsOrari (se dipendente autorizzato) possono usare LEGGE 104 PERMESSO con permessi speciali
    if ((isAdminViewer || ((isCoordinatoreViewer || isAbilitaInsOrariViewer) && isDipendenteAutorizzato104)) && 
        permessiSpeciali?.hasSpecial && permessiSpeciali.tipo === 1 && (permessiSpeciali.remainingGg ?? 0) > 0) {
      allowedTypes.push('LEGGE 104 PERMESSO');
    }
    return allowedTypes.includes(tipoOrario);
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, isDipendenteAutorizzato104]);
  
  // Data blocco applicabile per ruolo e DataControllo per dipendente.
  const [dataBloccoGlobale, setDataBloccoGlobale] = useState<dayjs.Dayjs | null>(null);
  const [dataBloccoCoordinatori, setDataBloccoCoordinatori] = useState<dayjs.Dayjs | null>(null);
  const [dataControllo, setDataControllo] = useState<dayjs.Dayjs | null>(null);
  const [eccezioneBloccoAutomaticoDa, setEccezioneBloccoAutomaticoDa] = useState<dayjs.Dayjs | null>(null);
  const [eccezioneBloccoAutomaticoA, setEccezioneBloccoAutomaticoA] = useState<dayjs.Dayjs | null>(null);
  
  useEffect(() => {
    const loadBlocchi = async () => {
      try {
        const [dataGlobale, dataCoordinatori] = await Promise.all([
          impostazioniGlobaliService.getDataBloccoGlobale(),
          impostazioniGlobaliService.getDataBloccoCoordinatori()
        ]);
        if (dataGlobale) {
          setDataBloccoGlobale(dayjs(dataGlobale));
        } else {
          setDataBloccoGlobale(null);
        }
        setDataBloccoCoordinatori(dataCoordinatori ? dayjs(dataCoordinatori) : null);
        
        // Legge DataControllo per il dipendente
        let b;
        if (isActingForAnother && viewedCodicePersonale) {
          b = await bloccoService.getByCodice(
            viewedCodicePersonale,
            selectedDate.month() + 1,
            selectedDate.year()
          );
        } else {
          b = await bloccoService.getMine();
        }
        if (b && b.dataControllo) {
          setDataControllo(dayjs(b.dataControllo));
        } else {
          setDataControllo(null);
        }
        setEccezioneBloccoAutomaticoDa(b?.eccezioneBloccoAutomaticoDa ? dayjs(b.eccezioneBloccoAutomaticoDa) : null);
        setEccezioneBloccoAutomaticoA(b?.eccezioneBloccoAutomaticoA ? dayjs(b.eccezioneBloccoAutomaticoA) : null);
      } catch (e) {
        setDataBloccoGlobale(null);
        setDataBloccoCoordinatori(null);
        setDataControllo(null);
        setEccezioneBloccoAutomaticoDa(null);
        setEccezioneBloccoAutomaticoA(null);
      }
    };
    loadBlocchi();
    window.addEventListener('data-controllo-saved', loadBlocchi);
    return () => window.removeEventListener('data-controllo-saved', loadBlocchi);
  }, [isActingForAnother, viewedCodicePersonale, selectedDate]);

  useEffect(() => {
    if (dataControlloOverride === undefined) return;
    setDataControllo(dataControlloOverride ? dayjs(dataControlloOverride) : null);
  }, [dataControlloOverride]);

  // Carica i dipendenti autorizzati per LEGGE 104 PERMESSO (per Coordinatori e abilitaInsOrari)
  useEffect(() => {
    const fetchAutorizzati104 = async () => {
      try {
        const codiciAutorizzati = await orariGeneratiService.getDipendentiAutorizzati104();
        setDipendentiAutorizzati104(codiciAutorizzati);
        console.log('MioOrario - Dipendenti autorizzati 104:', codiciAutorizzati);
      } catch (err) {
        console.error('Errore caricamento dipendenti autorizzati 104:', err);
        // In caso di errore, nessuno è autorizzato
        setDipendentiAutorizzati104([]);
      }
    };
    fetchAutorizzati104();
  }, []); // Solo all'avvio

  /**
   * Logica blocco righe:
   * - DATA_BLOCCO_GLOBALE: blocco hard solo per ruoli diversi da Coordinatore/Admin
   * - DATA_BLOCCO_COORDINATORI: blocco hard solo per Coordinatore
   * - DataControllo: blocco soft (Coordinatore non può modificare; Admin sees grey but can modify)
   * La funzione ritorna:
   * - 'hard' se data <= DATA_BLOCCO_GLOBALE
   * - 'soft' se data <= DataControllo (e non è già hard)
   * - null se non bloccato
   *
   * Admin bypasses hard date blocks only.
   */
  const getBlockType = (dateStr?: string | null): 'hard' | 'soft' | null => {
    if (!dateStr) return null;
    const d = dayjs(dateStr);

    // Admin keeps edit permission, but DataControllo remains visible as a grey soft lock.
    if (isAdminViewer) {
      return dataControllo && !d.isAfter(dataControllo, 'day') ? 'soft' : null;
    }

    if (isCoordinatoreViewer && dataBloccoCoordinatori && !d.isAfter(dataBloccoCoordinatori, 'day')) {
      return 'hard';
    }

    if (!isCoordinatoreViewer && dataBloccoGlobale && !d.isAfter(dataBloccoGlobale, 'day')) {
      return 'hard';
    }
    
    // Controlla DataControllo
    if (dataControllo && !d.isAfter(dataControllo, 'day')) {
      return 'soft';
    }
    
    return null;
  };

  // Verifica se la riga deve essere visualizzata come bloccata (grigia).
  const isDateBlocked = (dateStr?: string | null) => {
    return getBlockType(dateStr) !== null;
  };

  const isTurnoNellEccezioneBloccoAutomatico = (data: string) => {
    if (!isCoordinatoreViewer || !eccezioneBloccoAutomaticoDa || !eccezioneBloccoAutomaticoA) return false;
    const giorno = dayjs(data);
    return !giorno.isBefore(eccezioneBloccoAutomaticoDa, 'day') && !giorno.isAfter(eccezioneBloccoAutomaticoA, 'day');
  };

  const isTurnoBlocked = (turno: TurnoCreato) =>
    isDateBlocked(turno.data) || (Boolean(turno.bloccoAutomatico)
      && (!isAdminViewer || isActingForAnother)
      && !isTurnoNellEccezioneBloccoAutomatico(turno.data));

  // Verifica se l'utente corrente può MODIFICARE una riga bloccata.
  // Admin può sempre modificare; Coordinatore è limitato solo dal proprio cutoff.
  const canUserModifyBlockedRow = (dateStr?: string | null) => {
    if (isAdminViewer) return true;
    return getBlockType(dateStr) === null;
  };

  const canUserModifyBlockedTurno = (turno: TurnoCreato) =>
    isAdminViewer || !isTurnoBlocked(turno);
  const shouldMaskAssenze = isAbilitaInsOrariViewer;

  // Carica info dipendente per calcolo ore contratto
  useEffect(() => {
    const loadInfo = async () => {
      try {
        const all = await apiRequest<Dipendente[]>('/dipendenti');
        const nome = (overrideUser?.nome || userInfo.nome || '').trim();
        setDipendenteInfo(all.find(d => d.nome === nome) || null);
      } catch (e) {
        console.error('Errore caricamento dipendente per statistiche', e);
        setDipendenteInfo(null);
      }
    };
    loadInfo();
  }, [overrideUser?.nome, userInfo.nome]);


  // Esporta la tabella orari del mese corrente in CSV (solo admin visualizza il tasto)
  const exportOrariCSV = () => {
    try {
      const nomeDip = (overrideUser?.nome || userInfo?.nome || 'dipendente').replace(/\s+/g, '_');
      const meseAnno = selectedDate.format('YYYY-MM');
      const header = [
        'Data', 'Giorno', 'Turno', 'OraPrevInizio', 'OraPrevFine', 'OraConsInizio', 'OraConsFine', 'TipoOrario', 'Annotazioni', 'OrePrev', 'OreCons'
      ];
      const rows = (orari || [])
        .filter(r => !r.outOfMonth)
        .sort((a,b) => a.data.localeCompare(b.data))
        .map(r => {
          const data = dayjs(r.data);
          const orePrev = hhmmDiffToHours(r.oraPrevInizio || undefined, r.oraPrevFine || undefined);
          const oreCons = hhmmDiffToHours(r.oraConsInizio || undefined, r.oraConsFine || undefined);
          return [
            data.format('YYYY-MM-DD'),
            data.format('dddd'),
            r.turno || '',
            r.oraPrevInizio || '',
            r.oraPrevFine || '',
            r.oraConsInizio || '',
            r.oraConsFine || '',
            r.codiceTipoOrario || r.tipoOrario || '',
            (r.annotazioni || '').replace(/\r?\n/g, ' '),
            orePrev.toFixed(2),
            oreCons.toFixed(2)
          ];
        });
      const csv = [header, ...rows]
        .map(line => line.map(v => {
          const s = (v ?? '').toString();
          // Se contiene separatori o virgolette, racchiudi tra doppi apici e raddoppia le virgolette
          if (/[";,\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
          return s;
        }).join(';'))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orario-${nomeDip}-${meseAnno}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Errore esportazione CSV', e);
      alert('Impossibile esportare la tabella orari');
    }
  };

  // Effect per ripristinare la posizione dello scroll
  useEffect(() => {
    if (pendingScrollPosition !== null) {
      // Usa requestAnimationFrame per assicurarsi che il DOM sia aggiornato
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.scrollTo({
            top: pendingScrollPosition,
            behavior: 'auto'
          });
          setPendingScrollPosition(null);
        }, 50); // Piccolo delay per assicurarsi che il rendering sia completo
      });
    }
  }, [pendingScrollPosition, orari]); // Dipende da orari per essere sicuri che il re-render sia completato

  // Imposta mese/anno iniziali se forniti dai props (es. da OrarioDipendente)
  useEffect(() => {
    if (initialMonth && initialYear) {
      const newDate = dayjs().year(initialYear).month(initialMonth - 1).date(1);
      setSelectedDate(newDate);
    }
  }, [initialMonth, initialYear]);

  // Admin bypassa il lock di competenza (mesi precedenti)
  const isLocked = isAdminViewer ? false : isCompetenzaLocked(selectedDate.year(), selectedDate.month() + 1);

  // Carica l'impostazione globale dal backend
  useEffect(() => {
    const loadImpostazioneGlobale = async () => {
      try {
        const { impostazioniService } = await import('../api/impostazioniService');
        const impostazione = await impostazioniService.getTrascinaConfermareSempre();
        setEnableAlwaysConfirm(impostazione.enabled);
      } catch (error) {
        console.error('Errore nel caricamento dell\'impostazione globale:', error);
        // Fallback al localStorage per compatibilità
        try {
          const raw = localStorage.getItem('appSettings');
          if (raw) {
            const parsed = JSON.parse(raw);
            setEnableAlwaysConfirm(!!parsed.sliderAlwaysEnabled);
          }
        } catch {}
      }
    };
    
    loadImpostazioneGlobale();
  }, []);

  // Stato e caricamento permessi speciali per l'utente corrente
  const [permessiSpeciali, setPermessiSpeciali] = useState<{ hasSpecial: boolean; remainingGg?: number; tipo?: number } | null>(null);
  useEffect(() => {
    const loadPermessi = async () => {
      try {
        const month = selectedDate.format('YYYY-MM');
        const data = await apiRequest<any>(`/permessi-speciali/mine?month=${month}`);
        if (data) {
          setPermessiSpeciali({ hasSpecial: !!data.hasSpecial, remainingGg: data.remainingGg, tipo: data.tipo });
        } else {
          setPermessiSpeciali({ hasSpecial: false });
        }
      } catch (e) {
        setPermessiSpeciali({ hasSpecial: false });
      }
    };
    loadPermessi();
  }, [selectedDate]);

  // Stato e caricamento utilizzo LEGGE 104 per il mese corrente (per Admin/Coordinatore/abilitaInsOrari)
  const [utilizzo104, setUtilizzo104] = useState<Record<string, { ggMese: number; usedGg: number; remainingGg: number }>>({});
  const refetchUtilizzo104 = useCallback(async () => {
    if (!isAdminViewer && !isCoordinatoreViewer && !isAbilitaInsOrariViewer) return;
    try {
      const month = selectedDate.format('YYYY-MM');
      const data = await orariGeneratiService.getUtilizzo104(month);
      setUtilizzo104(data);
    } catch (e) {
      console.error('Errore caricamento utilizzo 104:', e);
    }
  }, [isAdminViewer, isCoordinatoreViewer, isAbilitaInsOrariViewer, selectedDate]);

  useEffect(() => {
    refetchUtilizzo104();
  }, [refetchUtilizzo104]);

  // Gestione click globale per salvare automaticamente quando si clicca fuori dalla riga in modifica
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Se non c'è nessuna riga in modifica, non fare nulla
      const editingRowIds = Object.keys(editingRows);
      if (editingRowIds.length === 0) return;

      // Ottieni l'ID della riga in modifica (dovrebbe essercene solo una)
      const editingRowId = parseInt(editingRowIds[0]);
      
      // Trova l'elemento della riga in modifica
      const rowElement = document.querySelector(`[data-row-id="${editingRowId}"]`);
      if (!rowElement) {
        return;
      }

      const target = event.target as HTMLElement;
      
      // Verifica se il click è avvenuto all'interno della riga in modifica
      if (rowElement.contains(target)) {
        return; // Click dentro la riga, non salvare
      }

      // Verifica se il click è sui pulsanti di azione (Salva/Annulla)
      if (target.closest('.editing-buttons')) {
        return; // Click sui pulsanti, non salvare (hanno la loro logica)
      }

      // Verifica se il click è sul menu a tendina del Select
      if (target.closest('.MuiPopover-root') || 
          target.closest('.MuiPaper-root') ||
          target.closest('[role="presentation"]') ||
          target.closest('[role="listbox"]')) {
        return; // Click sul menu del Select, non salvare
      }

      // Il click è fuori dalla riga, salva automaticamente
      // Usa setTimeout per permettere al DOM di aggiornarsi completamente
      setTimeout(() => {
        // Verifica ancora una volta che la riga sia in modifica
        if (editingRows[editingRowId]) {
          // Cerca prima il pulsante Salva (se showActionsForRow è impostato)
          const editingButtons = rowElement.querySelector('.editing-buttons');
          if (editingButtons) {
            const saveButton = Array.from(editingButtons.querySelectorAll('button')).find(btn => {
              const saveIcon = btn.querySelector('[data-testid="SaveIcon"]');
              return saveIcon !== null;
            });
            
            if (saveButton) {
              (saveButton as HTMLButtonElement).click();
              return;
            }
          }
          
          // Se i pulsanti non sono visibili, chiama direttamente saveConsuntivo
          // Questo accade quando si cambia solo il tipo orario senza cliccare su altri campi
          const saveFunc = async () => {
            try {
              const editData = editingRows[editingRowId];
              if (!editData) return;

              // Regola Categoria B: orari obbligatori
              if (isCategoriaB(editData.tipoOrario)) {
                if (!editData.oraConsInizio || !editData.oraConsFine) {
                  alert('Inserire Orario Inizio e Orario Fine');
                  setShowActionsForRow(editingRowId);
                  if (!editData.oraConsInizio) {
                    setActiveCell({ rowId: editingRowId, field: 'oraConsInizio' });
                  } else if (!editData.oraConsFine) {
                    setActiveCell({ rowId: editingRowId, field: 'oraConsFine' });
                  }
                  return;
                }
              }

              // Validazione
              const validationError = validateTimes(editData.oraConsInizio, editData.oraConsFine, editData.tipoOrario);
              if (validationError) {
                alert(validationError);
                return;
              }

              // Salva
              if (editingRowId > 0) {
                const params = new URLSearchParams();
                if (editData.tipoOrario === 'RIPOSO') {
                  params.append('oraConsInizio', '');
                  params.append('oraConsFine', '');
                } else if (!isCodiceTipoOrarioSpeciale(editData.tipoOrario) || shouldSendConsuntivoTimes(editData.tipoOrario, editData.oraConsInizio, editData.oraConsFine)) {
                  if (editData.oraConsInizio) params.append('oraConsInizio', editData.oraConsInizio);
                  if (editData.oraConsFine) params.append('oraConsFine', editData.oraConsFine);
                }
                if (editData.tipoOrario) params.append('codiceTipoOrario', editData.tipoOrario);
                if (editData.annotazioni) params.append('annotazioni', editData.annotazioni);
                const orarioCorrente1 = orari.find(o => o.id === editingRowId);
                const turnoParam = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario, orarioCorrente1?.turno);
                params.append('turno', turnoParam);

                let updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${editingRowId}/consuntivo?${params.toString()}`, { method: 'PUT' });

                if (editData.tipoOrario === 'RIPOSO' && (viewerInfo?.ruolo === 'Admin' || userInfo?.ruolo === 'Admin')) {
                  const turnoCompleto = { ...updatedTurno, oraPrevInizio: null, oraPrevFine: null, oraConsInizio: null, oraConsFine: null };
                  updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${editingRowId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(turnoCompleto) });
                }

                // TIPO_ASSENZA_PREIMPOSTA_ORARI (MALATTIA, FERIE, ecc.): persisti sempre oraPrevInizio/oraPrevFine
                if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(editData.tipoOrario) && dipendenteInfo) {
                  const orarioPerPrev = orari.find(o => o.id === editingRowId);
                  if (orarioPerPrev) {
                    const dayOfWeekPrev = dayjs(orarioPerPrev.data).day();
                    let contractHoursPrev = 0;
                    switch(dayOfWeekPrev) {
                      case 1: contractHoursPrev = dipendenteInfo.lunedi; break;
                      case 2: contractHoursPrev = dipendenteInfo.martedi; break;
                      case 3: contractHoursPrev = dipendenteInfo.mercoledi; break;
                      case 4: contractHoursPrev = dipendenteInfo.giovedi; break;
                      case 5: contractHoursPrev = dipendenteInfo.venerdi; break;
                      case 6: contractHoursPrev = dipendenteInfo.sabato; break;
                      case 0: contractHoursPrev = dipendenteInfo.domenica; break;
                    }
                    const newOraPrevInizio = '00:00:00';
                    const newOraPrevFine = contractHoursPrev > 0
                      ? dayjs().hour(0).minute(0).add(contractHoursPrev, 'hour').format('HH:mm:ss')
                      : '00:00:00';
                    const turnoConPrev = { ...updatedTurno, oraPrevInizio: newOraPrevInizio, oraPrevFine: newOraPrevFine };
                    updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${editingRowId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(turnoConPrev) });
                  }
                }

                const serverTipo = (updatedTurno as any).tipoOrario || (updatedTurno as any).codiceTipoOrario;
                const clientTipo = editData.tipoOrario;
                const safeTipoOrario = serverTipo && serverTipo !== 'ASSENZA' ? serverTipo : clientTipo;

                setOrari(prevOrari => prevOrari.map(orario => (
                  orario.id === editingRowId ? {
                    ...orario,
                    oraConsInizio: updatedTurno.oraConsInizio ? (updatedTurno.oraConsInizio.length > 5 ? updatedTurno.oraConsInizio.substring(0,5) : updatedTurno.oraConsInizio) : null,
                    oraConsFine: updatedTurno.oraConsFine ? (updatedTurno.oraConsFine.length > 5 ? updatedTurno.oraConsFine.substring(0,5) : updatedTurno.oraConsFine) : null,
                    oraPrevInizio: updatedTurno.oraPrevInizio ? (updatedTurno.oraPrevInizio.length > 5 ? updatedTurno.oraPrevInizio.substring(0,5) : updatedTurno.oraPrevInizio) : null,
                    oraPrevFine: updatedTurno.oraPrevFine ? (updatedTurno.oraPrevFine.length > 5 ? updatedTurno.oraPrevFine.substring(0,5) : updatedTurno.oraPrevFine) : null,
                    codiceTipoOrario: safeTipoOrario,
                    tipoOrario: safeTipoOrario,
                    annotazioni: updatedTurno.annotazioni || editData.annotazioni,
                    turno: updatedTurno.turno || turnoParam
                  } : orario
                )));
              }

              setEditingRows(prev => {
                const newState = { ...prev };
                delete newState[editingRowId];
                return newState;
              });
              setShowActionsForRow(null);
              setActiveCell(null);
            } catch (err) {
              console.error('Errore nel salvataggio automatico:', err);
              alert(getApiErrorMessage(err, 'Errore nel salvataggio'));
            }
          };
          
          saveFunc();
        }
      }, 100);
    };

    // Aggiungi il listener
    document.addEventListener('mousedown', handleGlobalClick);

    // Rimuovi il listener quando il componente viene smontato o quando editingRows cambia
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [editingRows, viewerInfo, userInfo]);


  // Funzioni per gestire le preferenze (memoizzate per evitare ricreazioni ad ogni render)
  const checkPreferencesPeriod = React.useCallback(() => {
    // Nuova regola: il dipendente può inserire preferenze solo dal 16 alla fine del mese corrente
    return dayjs().date() >= 16;
  }, []);

  const calculateTargetMonth = React.useCallback(() => {
    // Nuova regola: mese target = secondo mese successivo
    return dayjs().add(2, 'month').startOf('month');
  }, []);

  const checkIfTargetMonth = React.useCallback((selectedMonth: Dayjs, targetMonth: Dayjs) => {
    // Consentito solo per il mese esattamente pari al target (non altri mesi futuri)
    return selectedMonth.isSame(targetMonth, 'month');
  }, []);

  // Funzione per ottenere i colori dei turni (come in GenerazioneOrario)
  const getTurnoColor = (turno: string) => {
    switch (turno) {
      case 'Mattina': return '#fff59d'; // giallo chiaro
      case 'Pomeriggio': return '#81d4fa'; // azzurro chiaro
      case 'Intermezzo': return '#a5d6a7'; // verde chiaro
    case 'Riposo':
    case 'RIPOSO': return '#e0e0e0'; // grigio chiaro
      default: return '#e0e0e0';
    }
  };

  const getTurnoTextColor = (turno: string) => {
    switch (turno) {
      case 'Mattina': return '#000000'; // testo nero per giallo
      case 'Pomeriggio': return '#000000'; // testo nero per azzurro
      case 'Intermezzo': return '#000000'; // testo nero per verde
    case 'Riposo':
    case 'RIPOSO': return '#000000'; // testo nero per grigio
      default: return '#000000';
    }
  };

  // Verifica se una data è sabato o domenica
  const isWeekend = (dateString: string) => {
    const date = dayjs(dateString);
    const dayOfWeek = date.day(); // 0 = domenica, 6 = sabato
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Helper settimana (Lun-Dom)
  const getWeekRange = (dateStr: string) => {
    const d = dayjs(dateStr);
    const dow = d.day();
    const daysToMonday = (dow + 6) % 7;
    const monday = d.subtract(daysToMonday, 'day');
    const week: string[] = [];
    for (let i = 0; i < 7; i++) week.push(monday.add(i, 'day').format('YYYY-MM-DD'));
    return week;
  };
  const isSameWeek = (a: string, b: string) => getWeekRange(a)[0] === getWeekRange(b)[0];

  // Determina se mostrare la freccia "Copia orario previsto in consuntivo"
  const shouldShowCopyButton = (orario: TurnoCreato) => {
    // Non mostrare per CHIUSO DITTA: è gestito automaticamente dal backend
    if ((orario.codiceTipoOrario || orario.tipoOrario) === 'CHIUSO DITTA') {
      return false;
    }

    // Deve avere orari previsti
    if (!orario.oraPrevInizio || !orario.oraPrevFine) {
      return false;
    }
    
    // Non mostrare se orari previsti sono entrambi 00:00 (non ha senso consolidare)
    if (orario.oraPrevInizio?.substring(0, 5) === '00:00' && orario.oraPrevFine?.substring(0, 5) === '00:00') {
      return false;
    }
    
    // Non deve avere già orari consuntivi
    if (orario.oraConsInizio && orario.oraConsFine) {
      return false;
    }
    
    // Per assenze con consuntivo (ROL, LEGGE 104, AVIS, ecc.) MOSTRA il pulsante conferma
    if (isAssenzaConConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null)) {
      return true;
    }
    
    // Non per assenze con calcolo automatico (visivamente non mostriamo i consuntivi)
    if (isCodiceTipoOrarioSpeciale(orario.codiceTipoOrario || orario.tipoOrario || null)) {
      return false;
    }
    
    return true;
  };

  // Formatta la data con giorno della settimana e rimuove gli "00" finali per dati reali
  const formatDataColumn = (dataString: string, isRealData: boolean) => {
    const date = dayjs(dataString);
    let formattedDate = date.format('DD/MM/YYYY');
    
    // Se è un dato reale dal database, rimuovi gli ultimi "00" se presenti
    if (isRealData && formattedDate.slice(-2) === '00') {
      formattedDate = formattedDate.slice(0, -2);
    }
    
    // Aggiungi il giorno della settimana
    const dayName = date.format('dddd');
    const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    
    return `${capitalizedDayName} ${formattedDate}`;
  };

  // Verifica se il codice tipo orario richiede calcolo automatico (disabilita i campi orario)
  const isCodiceTipoOrarioSpeciale = (codiceTipoOrario: string | null): boolean => {
    return codiceTipoOrario ? CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario) : false;
  };

  // Verifica se i campi consuntivo devono essere disabilitati
  // Per le assenze con consuntivo (ROL, LEGGE 104, AVIS, ecc.) non disabilita
  const isDisableConsuntivo = (codiceTipoOrario: string | null): boolean => {
    if (!codiceTipoOrario) return false;
    // ASSENZA NON RETRIBUITA: disabilita completamente i campi consuntivo
    if (CODICI_DISABILITA_CONSUNTIVO.includes(codiceTipoOrario)) return true;
    // Se è un'assenza che permette consuntivo, NON disabilitare
    if (isAssenzaConConsuntivoFiltered(codiceTipoOrario)) return false;
    // Altrimenti usa la logica standard
    return CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario);
  };

  // Determina automaticamente il tipo di turno basandosi sull'ora di inizio e codice tipo orario
  const determinaTipoTurno = (oraConsInizio: string | null, codiceTipoOrario: string | null, fallbackTurno?: string | null): string => {
    // Se il codice tipo orario è RIPOSO, turno deve essere "Riposo"
    if (codiceTipoOrario === 'RIPOSO') {
      return 'Riposo';
    }

    // Se il tipo è una delle assenze specificate, il Turno deve essere sempre ASSENZA
    if (isAssenzaTipoFiltered(codiceTipoOrario)) {
      return 'ASSENZA';
    }

    // Se il codice tipo orario non è LAVORATO, mostra il codice specifico
    if (codiceTipoOrario && codiceTipoOrario !== 'LAVORATO') {
      return codiceTipoOrario;
    }

    // Se il codice tipo orario è LAVORATO, determina in base all'orario
    if (codiceTipoOrario === 'LAVORATO' && oraConsInizio) {
      // Converte l'ora in minuti dalla mezzanotte per facilitare il confronto
      const [ore, minuti] = oraConsInizio.split(':').map(Number);
      const minutiTotali = ore * 60 + minuti;

      if (minutiTotali < 600) {
        return 'Mattina';
      } else if (minutiTotali >= 600 && minutiTotali < 810) {
        return 'Intermezzo';
      } else {
        return 'Pomeriggio';
      }
    }

    // Se le ore cons sono vuote ma c'è un turno esistente (derivato dall'ora prev), lo preserva
    if (fallbackTurno && fallbackTurno !== 'ASSENZA') {
      return fallbackTurno;
    }

    // Caso di default
    return '';
  };

  // Genera una griglia vuota solo per il mese selezionato (1° giorno -> ultimo giorno)
  const generateEmptyMonthGrid = useCallback((month: Dayjs): TurnoCreato[] => {
    const monthStart = month.startOf('month');
    const monthEnd = month.endOf('month');
    const emptyOrari: TurnoCreato[] = [];
    let cursor = monthStart;
    while (cursor.isBefore(monthEnd) || cursor.isSame(monthEnd, 'day')) {
      emptyOrari.push({
        id: -parseInt(cursor.format('YYYYMMDD')),
        neg: userInfo.negozio || '',
        descrizione: userInfo.nome || '',
        data: cursor.format('YYYY-MM-DD'),
        oraConsInizio: null,
        oraConsFine: null,
        oraPrevInizio: null,
        oraPrevFine: null,
        giornoSettimana: cursor.format('dddd'),
        turno: '',
        utenteModifica: '',
        dataModifica: '',
        outOfMonth: false
      });
      cursor = cursor.add(1, 'day');
    }
    return emptyOrari;
  }, [userInfo.negozio, userInfo.nome]);

  // Funzione per caricare le statistiche dei cambi turno
  const loadCambiTurnoStats = useCallback(async () => {
    if (!showCambiTurno) return;
    try {
      const stats = await cambiTurnoService.getStatistiche();
      setCambiTurnoStats(stats);
    } catch (err) {
      console.error('Errore nel caricamento statistiche cambi turno:', err);
    }
  }, [showCambiTurno]);

  // Effect per caricare le statistiche dei cambi turno
  useEffect(() => {
    loadCambiTurnoStats();
  }, [loadCambiTurnoStats]);

  const fetchOrari = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      
      // Verifica periodo preferenze e calcola mese target
      const isPrefPeriod = checkPreferencesPeriod();
      const targetMth = calculateTargetMonth();
      const isTarget = checkIfTargetMonth(selectedDate, targetMth);
      
      setIsPreferencesPeriod(isPrefPeriod);
      setIsTargetMonth(isTarget);
      
  // Intervallo limitato al solo mese selezionato
  const meseInizio = selectedDate.startOf('month');
  const meseFine = selectedDate.endOf('month');
  const dataInizio = meseInizio;
  const dataFine = meseFine;
      const dataInizioStr = dataInizio.format('YYYY-MM-DD');
      const dataFineStr = dataFine.format('YYYY-MM-DD');
      
      try {
        // Genera sempre la griglia completa del mese
        const emptyGrid = generateEmptyMonthGrid(selectedDate);
        // Prova a caricare gli orari esistenti filtrando per codice personale (oldId)
        const oldId = (overrideUser as any)?.oldId || userInfo.oldId || '';
        const negozio = (overrideUser as any)?.negozio || userInfo.negozio || '';
        const negParam = isActingForAnother && negozio ? `&neg=${encodeURIComponent(negozio)}` : '';
        const rawData = await apiRequest<any[]>(`/orari-generati/codice/${encodeURIComponent(oldId)}?dataInizio=${dataInizioStr}&dataFine=${dataFineStr}${negParam}`);
        
        if (rawData && rawData.length > 0) {
          const data: TurnoCreato[] = rawData.map(item => {
            // Per record RIPOSO senza turno, nascondi il tipoOrario per visualizzarlo come "Disponibile"
            const isRiposoVuoto = isRiposoSenzaTurno({ turno: item.turno, codiceTipoOrario: item.codiceTipoOrario });
            return {
              ...item,
              data: item.data,
              tipoOrario: isRiposoVuoto ? undefined : (item.codiceTipoOrario || undefined),
              blocco: !!item.blocco,
              bloccoAutomatico: Boolean(item.bloccoAutomatico),
              preferenza: item.preferenza || 0
            };
          });
          const mergedGrid: TurnoCreato[] = [];
          emptyGrid.forEach(emptyDay => {
            const turniPerGiorno = data.filter(d => d.data === emptyDay.data);
            if (turniPerGiorno.length > 0) {
              mergedGrid.push(...turniPerGiorno.map(td => ({ ...td, outOfMonth: emptyDay.outOfMonth })));
            } else {
              mergedGrid.push(emptyDay);
            }
          });
          const sortedGrid = sortOrariByDate(mergedGrid);
          setOrari(sortedGrid);
          const count = sortedGrid.filter(o => isPreferenceTurno(o)).length;
          setPreferenceCount(count);
        } else {
          setOrari(emptyGrid);
          setPreferenceCount(0);
        }
      } catch (fetchError: any) {
        const emptyGrid = generateEmptyMonthGrid(selectedDate);
        setOrari(emptyGrid);
        setPreferenceCount(0);
      }
      
    } catch (err) {
      setError('Errore nel caricamento della pagina');
      console.error('Error in fetchOrari:', err);
      // Anche in caso di errore, mostra la griglia vuota
      const emptyGrid = generateEmptyMonthGrid(selectedDate);
      setOrari(emptyGrid);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [selectedDate, checkPreferencesPeriod, calculateTargetMonth, checkIfTargetMonth, generateEmptyMonthGrid, overrideUser, userInfo.oldId, userInfo.negozio, isActingForAnother, sortOrariByDate]);

  // Declarative query for background refetch ability and error boundaries
  const monthStart = selectedDate.startOf('month').format('YYYY-MM-DD');
  const monthEnd = selectedDate.endOf('month').format('YYYY-MM-DD');
  // Usa codice personale (oldId) invece del nome per filtrare i turni
  const codicePersonale = (overrideUser as any)?.oldId || userInfo.oldId || '';
  const { isLoading: turniLoading } = useTurniDipendenteByCodice(codicePersonale, monthStart, monthEnd);
  const { data: festivitaData } = useFestivita(monthStart, monthEnd);

  // Mappa mesi per API backend statistiche
  const mesiNomi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const meseNome = mesiNomi[selectedDate.month()];
  const annoStats = selectedDate.year();
  const nomeDipStats = (overrideUser?.nome || userInfo.nome || '').trim();

  // Usa API backend per statistiche (stessa logica di GenerazioneOrario)
  const { data: statsData, refetch: refetchStats } = useStatsDipendente(nomeDipStats, meseNome, annoStats);

  const applyStatsData = useCallback((data: any) => {
    if (!data) return;
    const roundMin = (v: number) => Math.round((v ?? 0) * 60) / 60;
    const nextStats = {
      oreContratto: roundMin(data.oreContratto),
      orePianificate: roundMin(data.orePianificate),
      oreConsuntivate: roundMin(data.oreConsuntivate),
      assenzeContrattuali: roundMin(data.assenzeContrattuali),
      attuale: roundMin(data.attuale),
      differenzaAttuale: roundMin(data.differenzaAttuale)
    };
    setStats(nextStats);
    onStatsChange?.(nextStats);
  }, [onStatsChange]);

  // Sincronizza statistiche dal backend allo stato locale
  useEffect(() => {
    applyStatsData(statsData);
  }, [statsData, applyStatsData]);

  const refreshAfterTurnoWrite = useCallback(async () => {
    const [, statsResult] = await Promise.all([
      fetchOrari({ silent: true }),
      refetchStats(),
      refetchUtilizzo104()
    ]);
    applyStatsData((statsResult as any)?.data);
  }, [fetchOrari, refetchStats, refetchUtilizzo104, applyStatsData]);




  // Gestione click outside per annullare editing - RIMOSSO (gestito da un altro useEffect più specifico)
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     const target = event.target as HTMLElement;
  //     // Se il click non è su un input time, select, textfield o su un pulsante di azione, salva automaticamente
  //     if (!target.closest('input[type="time"]') && 
  //         !target.closest('.editing-buttons') && 
  //         !target.closest('.time-cell') &&
  //         !target.closest('.MuiSelect-root') &&
  //         !target.closest('.MuiTextField-root') &&
  //         !target.closest('.MuiFormControl-root') &&
  //         !target.closest('.MuiMenuItem-root') &&
  //         !target.closest('.tipo-orario-cell') &&
  //         !target.closest('.annotazioni-cell')) {
  //       // Salva automaticamente tutti gli editing in corso
  //       Object.keys(editingRows).forEach(rowId => {
  //         const turnoId = parseInt(rowId);
  //         const editData = editingRows[turnoId];
  //         if (editData) {
  //           saveConsuntivoAuto(turnoId, editData);
  //         }
  //       });
  //       setEditingRows({});
  //       setShowActionsForRow(null);
  //       setActiveCell(null);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [editingRows, saveConsuntivoAuto]);  // Funzioni per gestire le preferenze
  const insertPreference = async (data: string, tipoTurno: string) => {
    try {
      const bodyTarget = isActingForAnother ? overrideUser?.nome : undefined;
      const resp = await preferenzeService.insertPreference(data, tipoTurno, bodyTarget, alwaysShowPreferences || undefined);
      const created = getPreferencePayload(resp);
      setOrari(prev => prev.map(o => (
        o.data === data
          ? ({
              ...o,
              id: created?.id ?? o.id,
              blocco: true,
              preferenza: 1,
              turno: created?.turno ?? tipoTurno,
              oraPrevInizio: created?.oraPrevInizio ?? o.oraPrevInizio,
              oraPrevFine: created?.oraPrevFine ?? o.oraPrevFine,
              codiceTipoOrario: created?.codiceTipoOrario ?? o.codiceTipoOrario,
              tipoOrario: created?.codiceTipoOrario ?? (o as any).tipoOrario
            } as any)
          : o
      )));
      setPreferenceCount(prev => prev + 1);
      setError(null);
      return { success: true } as any;
    } catch (err: any) {
      // Gestione errori
      const errorMsg = err.message || err.response?.data?.message || '';
      console.error('✗ Errore inserimento preferenza:', errorMsg);
      throw new Error(errorMsg || 'Errore nell\'inserimento della preferenza');
    }
  };

  const updatePreference = async (turnoId: number, nuovoTipoTurno: string) => {
    try {
      const resp = await preferenzeService.updatePreference(turnoId, nuovoTipoTurno);
      const updated = getPreferencePayload(resp);
      // Aggiorna solo quella preferenza con gli orari previsti e codice tipo orario restituiti.
      setOrari(prev => prev.map(o => (
        o.id === turnoId
          ? ({
              ...o,
              turno: updated?.turno ?? nuovoTipoTurno,
              oraPrevInizio: updated?.oraPrevInizio ?? o.oraPrevInizio,
              oraPrevFine: updated?.oraPrevFine ?? o.oraPrevFine,
              codiceTipoOrario: updated?.codiceTipoOrario ?? o.codiceTipoOrario,
              preferenza: updated?.preferenza ?? o.preferenza
            } as any)
          : o
      )));
      setError(null);
      return { success: true } as any;
    } catch (err: any) {
      throw new Error(err.message || 'Errore nella modifica della preferenza');
    }
  };

  // Aggiorna un record esistente con turno NULL e codiceTipoOrario = RIPOSO per trasformarlo in preferenza
  const updateRiposoToPreference = async (turnoId: number, nuovoTipoTurno: string) => {
    try {
      const resp = await preferenzeService.updateRiposoToPreference(turnoId, nuovoTipoTurno);
      const updated = getPreferencePayload(resp);
      setOrari(prev => prev.map(o => (
        o.id === turnoId
          ? ({
              ...o,
              blocco: true,
              preferenza: 1,
              turno: updated?.turno ?? nuovoTipoTurno,
              oraPrevInizio: updated?.oraPrevInizio ?? o.oraPrevInizio,
              oraPrevFine: updated?.oraPrevFine ?? o.oraPrevFine,
              codiceTipoOrario: updated?.codiceTipoOrario ?? o.codiceTipoOrario
            } as any)
          : o
      )));
      setPreferenceCount(prev => prev + 1);
      setError(null);
      return { success: true } as any;
    } catch (err: any) {
      throw new Error(err.message || 'Errore nell\'aggiornamento della preferenza');
    }
  };

  const deletePreference = async (turnoId: number) => {
    try {
      const current = orari.find(o => o.id === turnoId);
      if (!current) return { success: false } as any;
      
      // Cancella la preferenza lato backend (converte in turno baseline)
      const response = await preferenzeService.deletePreference(turnoId);
      
      // Il backend restituisce il turno baseline aggiornato
      if (response && response.turno) {
        const turnoBaseline = response.turno;
        
        // Aggiorna lo stato locale con il turno baseline
        setOrari(prev => prev.map(o => {
          if (o.id === turnoId) {
            return {
              ...turnoBaseline,
              id: turnoBaseline.id,
              blocco: turnoBaseline.blocco === 1,
              outOfMonth: o.outOfMonth,
              codiceTipoOrario: turnoBaseline.codiceTipoOrario || 'RIPOSO'
            } as TurnoCreato;
          }
          return o;
        }));
      }
      
      setPreferenceCount(prev => Math.max(0, prev - 1));
      setError(null);
      return { success: true } as any;
    } catch (err: any) {
      throw new Error(err.message || 'Errore nella cancellazione della preferenza');
    }
  };

  // Gestisce il click su una cella per inserire preferenza
  const handlePreferenceClick = async (orario: TurnoCreato) => {
    if (isTurnoBlocked(orario) && !canUserModifyBlockedTurno(orario)) return;

    // Solo se siamo nel periodo giusto e nel mese target o futuro (o bypass admin/coord)
    if (!canShowPreferences) {
      return;
    }

    // Regola: max 2 preferenze per SETTIMANA
    const weekPrefs = orari.filter(o => isPreferenceTurno(o) && isSameWeek(o.data, orario.data));
    if (weekPrefs.length >= 2) {
      // Apri dialog con le preferenze della settimana per cancellarne una
      setLimitDialogWeekPrefs(weekPrefs.map(w => ({ id: w.id, data: w.data, turno: w.turno })));
      setLimitDialogOpen(true);
      return;
    }

    // Se l'orario ha già un ID positivo e codiceTipoOrario definito, usa i dati locali
    let finalOrario = orario;
    let isExistingRiposoRecord = false;
    
    if (orario.id > 0) {
      // Record esistente - verifica se è RIPOSO (con o senza turno assegnato)
      const isTurnoNullOrEmpty = !orario.turno || orario.turno.trim() === '' || orario.turno === 'turnoVuoto';
      const isCodiceTipoOrarioRiposo = orario.codiceTipoOrario === 'RIPOSO';
      const isTurnoRiposo = (orario.turno || '').toLowerCase() === 'riposo';
      isExistingRiposoRecord = (isTurnoNullOrEmpty && isCodiceTipoOrarioRiposo) || (isTurnoRiposo && isCodiceTipoOrarioRiposo);
    } else {
      // ID negativo = riga vuota locale, devo verificare se esiste un record nel DB per questa data
      // Faccio una chiamata API per verificare se esiste un record RIPOSO per questa data
      try {
        const dataStr = orario.data;
        const oldIdCheck = (overrideUser as any)?.oldId || userInfo.oldId || '';
        const negozioCheck = (overrideUser as any)?.negozio || userInfo.negozio || '';
        const negParamCheck = negozioCheck ? `&neg=${encodeURIComponent(negozioCheck)}` : '';
        const checkData = await apiRequest<any[]>(`/orari-generati/codice/${encodeURIComponent(oldIdCheck)}?dataInizio=${dataStr}&dataFine=${dataStr}${negParamCheck}`);
        
        if (checkData && checkData.length > 0) {
          // Esiste un record nel DB per questa data
          const dbRecord = checkData[0];
          const isTurnoNullOrEmpty = !dbRecord.turno || dbRecord.turno.trim() === '';
          const isCodiceTipoOrarioRiposo = dbRecord.codiceTipoOrario === 'RIPOSO';
          const isTurnoRiposo = (dbRecord.turno || '').toLowerCase() === 'riposo';
          
          if ((isTurnoNullOrEmpty && isCodiceTipoOrarioRiposo) || (isTurnoRiposo && isCodiceTipoOrarioRiposo)) {
            // È un record RIPOSO (con o senza turno) - usa UPDATE
            isExistingRiposoRecord = true;
            // Aggiorna l'orario con i dati dal DB
            finalOrario = {
              ...orario,
              id: dbRecord.id,
              turno: dbRecord.turno,
              codiceTipoOrario: dbRecord.codiceTipoOrario,
              blocco: dbRecord.blocco,
              preferenza: dbRecord.preferenza
            };
          }
        }
      } catch (err) {
        // Nessun record esistente per questa data, procedo con INSERT
      }
    }
    
    setSelectedOrario(finalOrario);
    if (isExistingRiposoRecord) {
      // Caso speciale: record esistente con RIPOSO - usa UPDATE invece di INSERT
      setDialogType('updateRiposo');
    } else {
      // Riga vuota o senza turno - usa INSERT
      setDialogType('insert');
    }
    setTurnoDialogOpen(true);
  };

  // Gestisce il click sulle preferenze esistenti (icona ❤️)
  const handleExistingPreferenceClick = async (orario: TurnoCreato) => {
    if (!isPreferenceTurno(orario)) return;
    if (isTurnoBlocked(orario) && !canUserModifyBlockedTurno(orario)) return;

    setSelectedOrario(orario);
    setDialogType('modify');
    setTurnoDialogOpen(true);
  };

  // Gestisce la selezione del turno nel dialog
  const handleTurnoSelection = async (turno: string) => {
    if (!selectedOrario || preferenceSubmitting) return;

    setPreferenceSubmitting(true);
    try {
      if (dialogType === 'insert') {
        await insertPreference(selectedOrario.data, turno);
      } else if (dialogType === 'modify') {
        await updatePreference(selectedOrario.id, turno);
      } else if (dialogType === 'updateRiposo') {
        // Aggiorna un record esistente con turno NULL e codiceTipoOrario = RIPOSO
        await updateRiposoToPreference(selectedOrario.id, turno);
      }
      setError(null);
      setTurnoDialogOpen(false);
      setSelectedOrario(null);
      await refreshAfterTurnoWrite();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPreferenceSubmitting(false);
    }
  };

  // Gestisce la cancellazione della preferenza
  const handleDeletePreference = async () => {
    if (!selectedOrario || !isPreferenceTurno(selectedOrario) || preferenceSubmitting) return;

    const turnoId = selectedOrario.id;
    setPreferenceSubmitting(true);
    setTurnoDialogOpen(false);
    setSelectedOrario(null);
    try {
      await deletePreference(turnoId);
      setError(null);
      await refreshAfterTurnoWrite();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPreferenceSubmitting(false);
    }
  };

  useEffect(() => {
    if (userInfo.nome) {
      fetchOrari();
    }
  }, [userInfo.nome, selectedDate, fetchOrari]);

  // Effetto per gestire il focus quando activeCell cambia
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeCell) {
      // Aspetta un tick per permettere al campo di essere renderizzato
      setTimeout(() => {
        const fieldName = activeCell.field === 'oraConsInizio' ? 'oraConsInizio' : 'oraConsFine';
        const input = document.querySelector(`input[data-field="${fieldName}"][data-row="${activeCell.rowId}"]`) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 0);
    }
  }, [activeCell]);

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5); // Mostra solo HH:MM
  };

  // Funzione per calcolare il colore di sfondo delle celle
  const getCellBackgroundColor = (consValue: string | null, prevValue: string | null) => {
    // Colori richiesti (50% trasparenza)
    const GREEN_50 = 'rgba(76, 175, 80, 0.5)'; // verde chiaro 50%
    const RED_50 = 'rgba(244, 67, 54, 0.5)';  // rosso chiaro 50%

    // Se non c'è valore consuntivo: verde chiaro 50%
    if (!consValue) {
      return GREEN_50;
    }

    // Se c'è valore consuntivo ma NON c'è valore previsto: ROSSO (differenza rilevata)
    if (!prevValue) {
      return RED_50;
    }

    // Confronta i valori (solo HH:MM)
    const consTime = consValue.substring(0, 5);
    const prevTime = prevValue.substring(0, 5);

    return consTime === prevTime ? GREEN_50 : RED_50;
  };

  const calculateTotalHours = (oraInizio: string | null, oraFine: string | null): string => {
    if (!oraInizio || !oraFine) return '-';
    
    const start = dayjs(`2000-01-01 ${oraInizio}`);
    const end = dayjs(`2000-01-01 ${oraFine}`);
    
    if (end.isBefore(start)) {
      // Se l'ora di fine è prima dell'inizio, assumiamo che attraversi mezzanotte
      const endNextDay = end.add(1, 'day');
      const diff = endNextDay.diff(start, 'minutes');
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } else {
      const diff = end.diff(start, 'minutes');
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
  };

  // Calcolo del totale ore per la colonna "Tot ore"
  const totalOreSum = useMemo(() => {
    let totalMinutes = 0;
    
    orari.forEach((orario) => {
      // Salta i RIPOSO
      if (orario.turno?.toUpperCase?.() === 'RIPOSO') return;
      
      const oraInizio = orario.oraConsInizio;
      const oraFine = orario.oraConsFine;
      
      if (!oraInizio || !oraFine) return;
      
      const start = dayjs(`2000-01-01 ${oraInizio}`);
      const end = dayjs(`2000-01-01 ${oraFine}`);
      
      if (end.isBefore(start)) {
        // Se l'ora di fine è prima dell'inizio, assumiamo che attraversi mezzanotte
        const endNextDay = end.add(1, 'day');
        totalMinutes += endNextDay.diff(start, 'minutes');
      } else {
        totalMinutes += end.diff(start, 'minutes');
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }, [orari]);

  const startEditing = (turnoId: number, oraConsInizio: string | null, oraConsFine: string | null) => {
    // Converte il formato da hh:mm:ss a hh:mm se necessario
    const formatTimeForInput = (time: string | null) => {
      if (!time) return '';
      return time.substring(0, 5); // Mantiene solo HH:MM
    };

    setEditingRows(prev => ({
      ...prev,
      [turnoId]: {
        oraConsInizio: formatTimeForInput(oraConsInizio),
        oraConsFine: formatTimeForInput(oraConsFine),
        tipoOrario: orari.find(o => o.id === turnoId)?.tipoOrario || '',
        annotazioni: orari.find(o => o.id === turnoId)?.annotazioni || ''
      }
    }));
  };

  // Controlla se un orario può essere modificato in base alla data e alle impostazioni
  const canEditOrario = (dataOrario: string): boolean => {
    if (isLocked) return false;
    // Blocco: verifica con la nuova logica (dataBlocco blocco hard, dataControllo blocco soft)
    if (isDateBlocked(dataOrario) && !canUserModifyBlockedRow(dataOrario)) return false;
    // Se l'impostazione globale è attiva, permetti sempre
    if (enableAlwaysConfirm) {
      return true;
    }
    
    // Altrimenti permetti solo per giorni con data <= oggi
    const today = dayjs();
    const orarioDate = dayjs(dataOrario);
    return orarioDate.isBefore(today, 'day') || orarioDate.isSame(today, 'day');
  };

  // Funzione per gestire il click su una cella
  const handleCellClick = (turnoId: number, field: 'oraConsInizio' | 'oraConsFine' | 'tipoOrario' | 'annotazioni', currentValue: string | null) => {
    if (isLocked) { return; }
    // Controlla se la data può essere modificata
    const turno = orari.find(t => t.id === turnoId);
    if (!turno || !canEditOrario(turno.data)) {
      return; // Non permettere l'editing se la data non è modificabile
    }

    // Inizializza l'editing se non esiste già
    if (!editingRows[turnoId]) {
      if (turno) {
        startEditing(turnoId, turno.oraConsInizio, turno.oraConsFine);
      }
    }
    setActiveCell({ rowId: turnoId, field });
    setShowActionsForRow(turnoId);
  };

  // Gestione del Tab
  const handleKeyDown = (event: React.KeyboardEvent, turnoId: number, field: 'oraConsInizio' | 'oraConsFine' | 'tipoOrario' | 'annotazioni') => {
    if (event.key === 'Tab') {
      event.preventDefault();
      
      if (field === 'oraConsInizio') {
        // Passa alla cella Ora_Cons_Fine
        setActiveCell({ rowId: turnoId, field: 'oraConsFine' });
      } else if (field === 'oraConsFine') {
        // Passa alla cella Tipo Orario
        setActiveCell({ rowId: turnoId, field: 'tipoOrario' });
      } else if (field === 'tipoOrario') {
        // Passa alla cella Annotazioni
        setActiveCell({ rowId: turnoId, field: 'annotazioni' });
      } else if (field === 'annotazioni') {
        // Salva automaticamente e esce dall'editing
        const editData = editingRows[turnoId];
        if (editData) {
          saveConsuntivoAuto(turnoId, editData).then((success) => {
            if (success) {
              setEditingRows(prev => {
                const newState = { ...prev };
                delete newState[turnoId];
                return newState;
              });
              setShowActionsForRow(null);
              setActiveCell(null);
            }
          });
        }
      }
    }
  };

  const copyPrevToConsuntivo = async (turnoId: number, oraPrevInizio: string | null, oraPrevFine: string | null) => {
  if (isLocked) { return; }
    // Converte il formato da hh:mm:ss a hh:mm se necessario
    const formatTimeForInput = (time: string | null) => {
      if (!time) return '';
      return time.substring(0, 5); // Mantiene solo HH:MM
    };

    // Trova l'orario corrente per determinare il tipo orario automatico
    const orarioCorrente = orari.find(o => o.id === turnoId);
    let tipoOrarioAutomatico = orarioCorrente?.tipoOrario || '';
    
    // Se ci sono orari previsti e il tipo orario non è ancora impostato, imposta automaticamente "LAVORATO"
    if (!tipoOrarioAutomatico && oraPrevInizio && oraPrevFine) {
      tipoOrarioAutomatico = 'LAVORATO';
    }
    // Fallback: se il tipo orario non è ancora stato compilato e il turno è lavorativo, imposta "LAVORATO"
    else if (!tipoOrarioAutomatico && orarioCorrente?.turno) {
      const turnoLower = orarioCorrente.turno.toLowerCase();
      if (turnoLower.includes('mattina') || turnoLower.includes('pomeriggio') || turnoLower.includes('intermezzo')) {
        tipoOrarioAutomatico = 'LAVORATO';
      }
    }

    // Determina gli orari consuntivi da usare
    let oraConsInizioValue = formatTimeForInput(oraPrevInizio);
    let oraConsFineValue = formatTimeForInput(oraPrevFine);

    // Per ASSENZE CON CONSUNTIVO (ROL, AVIS, ecc. - esclusa LEGGE 104 che usa solo Prev), calcola le ore rimanenti
    const CODICI_AUTOCOMPILA_CONSUNTIVO = ['ROL', 'AVIS', 'LUTTO', 'PERMESSI PARENTALI', 'PERMESSI ELETTORALI', 'PERMESSI STUDIO', 'LEGGE 118 PERMESSO'];
    if (CODICI_AUTOCOMPILA_CONSUNTIVO.includes(tipoOrarioAutomatico) && dipendenteInfo && orarioCorrente) {
      // Ottieni ore contrattuali per il giorno
      const dayOfWeek = dayjs(orarioCorrente.data).day();
      let contractHours = 0;
      switch(dayOfWeek) {
        case 1: contractHours = dipendenteInfo.lunedi; break;
        case 2: contractHours = dipendenteInfo.martedi; break;
        case 3: contractHours = dipendenteInfo.mercoledi; break;
        case 4: contractHours = dipendenteInfo.giovedi; break;
        case 5: contractHours = dipendenteInfo.venerdi; break;
        case 6: contractHours = dipendenteInfo.sabato; break;
        case 0: contractHours = dipendenteInfo.domenica; break;
      }

      // DEFAULT: Se non c'è LAVORATO, usa 00:00 -> ore contrattuali
      if (contractHours > 0) {
        const start = dayjs().hour(0).minute(0);
        const end = start.add(contractHours, 'hour');
        oraConsInizioValue = start.format('HH:mm');
        oraConsFineValue = end.format('HH:mm');
      }

      // Cerca un turno LAVORATO nello stesso giorno
      const lavoratoShift = orari.find(o => 
        o.data === orarioCorrente.data && 
        o.id !== turnoId && 
        (o.tipoOrario === 'LAVORATO' || o.codiceTipoOrario === 'LAVORATO') &&
        o.oraConsInizio && o.oraConsFine
      );

      if (lavoratoShift && lavoratoShift.oraConsInizio && lavoratoShift.oraConsFine) {
        // Calcola ore lavorate
        const start = dayjs(`2000-01-01 ${lavoratoShift.oraConsInizio}`);
        const end = dayjs(`2000-01-01 ${lavoratoShift.oraConsFine}`);
        const workedMinutes = end.diff(start, 'minute');
        const workedHours = workedMinutes / 60;

        // Se ore lavorate < ore contrattuali, riempi la differenza
        if (contractHours > workedHours) {
          const remainingHours = contractHours - workedHours;
          const newStart = end;
          const newEnd = newStart.add(remainingHours, 'hour');
          
          oraConsInizioValue = newStart.format('HH:mm');
          oraConsFineValue = newEnd.format('HH:mm');
        }
      }
    }

    const editData = {
      oraConsInizio: oraConsInizioValue,
      oraConsFine: oraConsFineValue,
      tipoOrario: tipoOrarioAutomatico,
      annotazioni: orarioCorrente?.annotazioni || ''
    };

    // Salva automaticamente senza passare per lo stato di editing
    const success = await saveConsuntivoAuto(turnoId, editData);
    
    // Se il salvataggio fallisce, apri la modalità editing per permettere correzioni manuali
    if (!success) {
      setShowActionsForRow(turnoId);
      setEditingRows(prev => ({
        ...prev,
        [turnoId]: editData
      }));
    }
  };

  const validateTimes = (oraInizio: string, oraFine: string, tipoOrario?: string): string | null => {
    // Se il tipo orario è RIPOSO, non servono gli orari
    if (tipoOrario === 'RIPOSO') {
      return null;
    }
    
    // Se il tipo orario è uno speciale (es. FERIE, ROL, ecc.), gli orari potrebbero essere auto-calcolati
    // Permettiamo il salvataggio anche senza orari per questi tipi
    if (tipoOrario && isCodiceTipoOrarioSpeciale(tipoOrario)) {
      // Questi tipi orario vengono calcolati automaticamente dal backend
      return null;
    }
    
    // Per LAVORATO: permettiamo il salvataggio anche senza ore consuntivo
    // Il tipoOrario è obbligatorio, ma oraInizio e oraFine possono essere vuoti
    if (!tipoOrario) {
      return 'Compilare il campo Tipo Orario';
    }
    
    // Se gli orari sono parzialmente compilati (uno sì e uno no), errore
    if ((oraInizio && !oraFine) || (!oraInizio && oraFine)) {
      return 'Compilare entrambi gli orari (Inizio e Fine) oppure lasciarli entrambi vuoti';
    }
    
    if (oraInizio && oraFine) {
      const startTime = dayjs(`2000-01-01 ${oraInizio}`);
      const endTime = dayjs(`2000-01-01 ${oraFine}`);
      
      if (endTime.isBefore(startTime)) {
        return "L'orario di uscita non può essere precedente all'orario di entrata";
      }
    }
    
    return null;
  };

  const saveConsuntivoAuto = useCallback(async (turnoId: number, editData: {oraConsInizio: string, oraConsFine: string, tipoOrario: string, annotazioni: string}) => {
    // Validazione
    const validationError = validateTimes(editData.oraConsInizio, editData.oraConsFine, editData.tipoOrario);
    if (validationError) {
      // Non salvare se c'è un errore di validazione
      console.warn("Validazione fallita in saveConsuntivoAuto:", validationError);
      return false;
    }

    try {
      if (turnoId > 0) {
        // UPDATE: Record esistente nel database
        const params = new URLSearchParams();

        // Se RIPOSO, cancella gli orari
        if (editData.tipoOrario === 'RIPOSO') {
          params.append('oraConsInizio', '');
          params.append('oraConsFine', '');
        } else if (!isCodiceTipoOrarioSpeciale(editData.tipoOrario) || isAssenzaConConsuntivoFiltered(editData.tipoOrario)) {
          // Se NON è speciale OPPURE è un'assenza che permette consuntivo (ROL, AVIS, CORSI AI DIPENDENTI, ecc.), invia gli orari
          if (editData.oraConsInizio) params.append('oraConsInizio', editData.oraConsInizio);
          if (editData.oraConsFine) params.append('oraConsFine', editData.oraConsFine);
        }

        if (editData.tipoOrario) params.append('codiceTipoOrario', editData.tipoOrario);
        if (editData.annotazioni) params.append('annotazioni', editData.annotazioni);

        // Turno: per assenze sempre ASSENZA; per lavorato calcolato da ora; altri: codice
        const orarioCorrenteAuto = orari.find(o => o.id === turnoId);
        const turnoParam = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario, orarioCorrenteAuto?.turno);
        params.append('turno', turnoParam);

        if (isLocked) throw new Error(LOCK_MESSAGE);
        const updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}/consuntivo?${params.toString()}`, { method: 'PUT' });

        // Stabilisce il tipo orario da mostrare: non sostituire con 'ASSENZA'
        const serverTipo = (updatedTurno as any).tipoOrario || (updatedTurno as any).codiceTipoOrario;
        const clientTipo = editData.tipoOrario;
        const safeTipoOrario = serverTipo && serverTipo !== 'ASSENZA' ? serverTipo : clientTipo;

        // TIPO_ASSENZA_PREIMPOSTA_ORARI (MALATTIA, FERIE, ecc.): persisti sempre oraPrevInizio/oraPrevFine
        // calcolati dalle ore contrattuali (00:00 → ore contratto), sovrascrivendo qualsiasi valore precedente
        let finalTurnoAuto = updatedTurno;
        if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(editData.tipoOrario) && dipendenteInfo) {
          const orarioPerPrev = orari.find(o => o.id === turnoId);
          if (orarioPerPrev) {
            const dayOfWeekPrev = dayjs(orarioPerPrev.data).day();
            let contractHoursPrev = 0;
            switch(dayOfWeekPrev) {
              case 1: contractHoursPrev = dipendenteInfo.lunedi; break;
              case 2: contractHoursPrev = dipendenteInfo.martedi; break;
              case 3: contractHoursPrev = dipendenteInfo.mercoledi; break;
              case 4: contractHoursPrev = dipendenteInfo.giovedi; break;
              case 5: contractHoursPrev = dipendenteInfo.venerdi; break;
              case 6: contractHoursPrev = dipendenteInfo.sabato; break;
              case 0: contractHoursPrev = dipendenteInfo.domenica; break;
            }
            const newOraPrevInizio = '00:00:00';
            const newOraPrevFine = contractHoursPrev > 0
              ? dayjs().hour(0).minute(0).add(contractHoursPrev, 'hour').format('HH:mm:ss')
              : '00:00:00';
            const turnoConPrev = { ...updatedTurno, oraPrevInizio: newOraPrevInizio, oraPrevFine: newOraPrevFine };
            if (isLocked) throw new Error(LOCK_MESSAGE);
            finalTurnoAuto = await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(turnoConPrev)
            });
          }
        }

        // Aggiorna lo stato con i valori restituiti dal backend (incluso calcolo automatico)
        setOrari(prevOrari => prevOrari.map(orario => (
          orario.id === turnoId
            ? {
                ...orario,
                oraConsInizio: finalTurnoAuto.oraConsInizio ? (finalTurnoAuto.oraConsInizio.length > 5 ? finalTurnoAuto.oraConsInizio.substring(0,5) : finalTurnoAuto.oraConsInizio) : null,
                oraConsFine: finalTurnoAuto.oraConsFine ? (finalTurnoAuto.oraConsFine.length > 5 ? finalTurnoAuto.oraConsFine.substring(0,5) : finalTurnoAuto.oraConsFine) : null,
                codiceTipoOrario: safeTipoOrario,
                tipoOrario: safeTipoOrario,
                annotazioni: finalTurnoAuto.annotazioni || editData.annotazioni,
                turno: finalTurnoAuto.turno || turnoParam
              }
            : orario
        )));
      } else {
        // CREATE: Nuova riga fittizia, crea un nuovo record
        const orario = orari.find(o => o.id === turnoId);
        if (!orario) {
          console.error('Errore: impossibile trovare i dati della riga');
          return false;
        }

        // Prepara i dati base per POST
        const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario);
        // Per i tipi assenza (incluso LEGGE 104 PERMESSO), forza sempre turno='ASSENZA'
        const turnoField = editData.tipoOrario && isAssenzaTipoFiltered(editData.tipoOrario) ? 'ASSENZA' : tipoTurnoAutomatico;
        
        // Per LEGGE 104 PERMESSO, calcola oraPrevInizio/oraPrevFine dalle ore contrattuali
        let oraPrevInizioValue = null;
        let oraPrevFineValue = null;
        if (editData.tipoOrario === 'LEGGE 104 PERMESSO' && dipendenteInfo) {
          const dayOfWeek = dayjs(orario.data).day();
          let contractHours = 0;
          switch(dayOfWeek) {
            case 1: contractHours = dipendenteInfo.lunedi; break;
            case 2: contractHours = dipendenteInfo.martedi; break;
            case 3: contractHours = dipendenteInfo.mercoledi; break;
            case 4: contractHours = dipendenteInfo.giovedi; break;
            case 5: contractHours = dipendenteInfo.venerdi; break;
            case 6: contractHours = dipendenteInfo.sabato; break;
            case 0: contractHours = dipendenteInfo.domenica; break;
          }
          if (contractHours > 0) {
            const start = dayjs().hour(0).minute(0);
            const end = start.add(contractHours, 'hour');
            oraPrevInizioValue = start.format('HH:mm');
            oraPrevFineValue = end.format('HH:mm');
          } else {
            oraPrevInizioValue = '00:00';
            oraPrevFineValue = '00:00';
          }
        }
        
        const newTurnoData = {
          neg: userInfo.negozio || '',
          descrizione: userInfo.nome || '',
          data: orario.data,
          oraConsInizio: editData.tipoOrario === 'RIPOSO' ? null : (isCodiceTipoOrarioSpeciale(editData.tipoOrario) && !shouldSendConsuntivoTimes(editData.tipoOrario, editData.oraConsInizio, editData.oraConsFine) ? null : (editData.oraConsInizio || null)),
          oraConsFine: editData.tipoOrario === 'RIPOSO' ? null : (isCodiceTipoOrarioSpeciale(editData.tipoOrario) && !shouldSendConsuntivoTimes(editData.tipoOrario, editData.oraConsInizio, editData.oraConsFine) ? null : (editData.oraConsFine || null)),
          oraPrevInizio: oraPrevInizioValue,
          oraPrevFine: oraPrevFineValue,
          giornoSettimana: orario.giornoSettimana,
          turno: turnoField,
          utenteModifica: userInfo.nome || '',
          codiceTipoOrario: editData.tipoOrario || null,
          annotazioni: editData.annotazioni || null
        };

        if (isLocked) throw new Error(LOCK_MESSAGE);
        const created = await apiRequest<TurnoCreato>('/orari-generati', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newTurnoData)
        });

        // Se codice speciale (esclusi ROL e LEGGE 104 PERMESSO già completi nel POST),
        // trigger auto-calcolo con PUT consuntivo per impostare gli orari previsti automatici
        let createdUpdated = created;
        if (created && isCodiceTipoOrarioSpeciale(editData.tipoOrario) && editData.tipoOrario !== 'LEGGE 104 PERMESSO' && editData.tipoOrario !== 'ROL') {
          const afterParams = new URLSearchParams();
          afterParams.append('codiceTipoOrario', editData.tipoOrario);
          if (editData.annotazioni) afterParams.append('annotazioni', editData.annotazioni);
          afterParams.append('turno', turnoField);

          if (isLocked) throw new Error(LOCK_MESSAGE);
          createdUpdated = await apiRequest<TurnoCreato>(`/orari-generati/${created.id}/consuntivo?${afterParams.toString()}`, { method: 'PUT' });
        }

        // Stabilisce il tipo orario da mostrare per la riga creata: non sostituire con 'ASSENZA'
        const serverTipoCreated = (createdUpdated as any).tipoOrario || (createdUpdated as any).codiceTipoOrario;
        const clientTipoCreated = editData.tipoOrario;
        const safeTipoOrarioCreated = serverTipoCreated && serverTipoCreated !== 'ASSENZA' ? serverTipoCreated : clientTipoCreated;

        // Aggiorna la riga fittizia con l'oggetto definitivo
        setOrari(prevOrari => prevOrari.map(o => (
          o.id === turnoId
            ? {
                ...o,
                id: createdUpdated.id || created.id,
                oraConsInizio: createdUpdated.oraConsInizio ? (createdUpdated.oraConsInizio.length > 5 ? createdUpdated.oraConsInizio.substring(0,5) : createdUpdated.oraConsInizio) : null,
                oraConsFine: createdUpdated.oraConsFine ? (createdUpdated.oraConsFine.length > 5 ? createdUpdated.oraConsFine.substring(0,5) : createdUpdated.oraConsFine) : null,
                codiceTipoOrario: safeTipoOrarioCreated,
                tipoOrario: safeTipoOrarioCreated,
                annotazioni: createdUpdated.annotazioni || editData.annotazioni || undefined,
                turno: createdUpdated.turno || tipoTurnoAutomatico
              } as TurnoCreato
            : o
        )));
      }
      
      // Aggiorna le statistiche dopo il salvataggio
      await refreshAfterTurnoWrite();

      return true;
      
    } catch (err) {
      console.error('Error saving consuntivo auto:', err);
      alert(getApiErrorMessage(err, 'Errore nel salvataggio'));
      return false;
    }
  }, [orari, userInfo.negozio, userInfo.nome, isLocked, refreshAfterTurnoWrite]);

  const saveConsuntivo = async (turnoId: number) => {
    try {
      const editData = editingRows[turnoId];
      if (!editData) return;

      // Regola Categoria B: orari obbligatori
      if (isCategoriaB(editData.tipoOrario)) {
        if (!editData.oraConsInizio || !editData.oraConsFine) {
          alert('Inserire Orario Inizio e Orario Fine');
          setShowActionsForRow(turnoId);
          if (!editData.oraConsInizio) {
            setActiveCell({ rowId: turnoId, field: 'oraConsInizio' });
          } else if (!editData.oraConsFine) {
            setActiveCell({ rowId: turnoId, field: 'oraConsFine' });
          }
          return;
        }
      }

      // Validazione
      const validationError = validateTimes(editData.oraConsInizio, editData.oraConsFine, editData.tipoOrario);
      if (validationError) {
        alert(validationError);
        return;
      }

      // Validazione ROL su riga duplicata: ROL + altri turni del giorno <= ore contratto
      if (editData.tipoOrario === 'ROL' && editData.oraConsInizio && editData.oraConsFine && dipendenteInfo) {
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario?.annotazioni === '__RIGA_DUPLICATA__') {
          const dataGiorno = currentOrario.data;
          const dayOfWeek = dayjs(dataGiorno).day();
          let contractHours = 0;
          switch (dayOfWeek) {
            case 1: contractHours = dipendenteInfo.lunedi; break;
            case 2: contractHours = dipendenteInfo.martedi; break;
            case 3: contractHours = dipendenteInfo.mercoledi; break;
            case 4: contractHours = dipendenteInfo.giovedi; break;
            case 5: contractHours = dipendenteInfo.venerdi; break;
            case 6: contractHours = dipendenteInfo.sabato; break;
            case 0: contractHours = dipendenteInfo.domenica; break;
          }
          const contractMinutes = contractHours * 60;

          const rolStart = dayjs(`2000-01-01 ${editData.oraConsInizio}`);
          const rolEnd = dayjs(`2000-01-01 ${editData.oraConsFine}`);
          const rolMinutes = rolEnd.diff(rolStart, 'minutes');

          // Somma ore degli altri turni dello stesso giorno (consuntivo se disponibile, altrimenti preventivato)
          const altriTurniMinutes = orari
            .filter(o => o.id !== turnoId && o.data === dataGiorno)
            .reduce((acc, o) => {
              const inizio = o.oraConsInizio || o.oraPrevInizio;
              const fine = o.oraConsFine || o.oraPrevFine;
              if (!inizio || !fine) return acc;
              const s = dayjs(`2000-01-01 ${inizio}`);
              const e = dayjs(`2000-01-01 ${fine}`);
              return acc + Math.max(0, e.diff(s, 'minutes'));
            }, 0);

          if (rolMinutes + altriTurniMinutes > contractMinutes) {
            const totaleOre = ((rolMinutes + altriTurniMinutes) / 60).toFixed(2);
            const contrattoOre = contractHours.toFixed(2);
            alert(`Impossibile salvare: il totale ore del giorno (${totaleOre}h) supera le ore contrattuali (${contrattoOre}h).\nRidurre le ore ROL.`);
            return;
          }
        }
      }

      if (turnoId > 0) {
        // UPDATE: Record esistente nel database
        const params = new URLSearchParams();

        // Se RIPOSO, cancella gli orari
        if (editData.tipoOrario === 'RIPOSO') {
          params.append('oraConsInizio', '');
          params.append('oraConsFine', '');
        } else if (!isCodiceTipoOrarioSpeciale(editData.tipoOrario) || isAssenzaConConsuntivo(editData.tipoOrario)) {
          // Se NON è speciale OPPURE è un'assenza che permette consuntivo (ROL, AVIS, CORSI AI DIPENDENTI, ecc.), invia gli orari
          if (editData.oraConsInizio) params.append('oraConsInizio', editData.oraConsInizio);
          if (editData.oraConsFine) params.append('oraConsFine', editData.oraConsFine);
        }

        if (editData.tipoOrario) params.append('codiceTipoOrario', editData.tipoOrario);
        if (editData.annotazioni) params.append('annotazioni', editData.annotazioni);

  // Turno: per assenze sempre ASSENZA; per lavorato calcolato da ora; altri: codice
  const orarioCorrenteSave = orari.find(o => o.id === turnoId);
  const turnoParam = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario, orarioCorrenteSave?.turno);
        params.append('turno', turnoParam);

  let updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}/consuntivo?${params.toString()}`, { method: 'PUT' });

  // Se Admin imposta RIPOSO, azzera anche gli orari previsti con una chiamata PUT completa
  if (editData.tipoOrario === 'RIPOSO' && (viewerInfo?.ruolo === 'Admin' || userInfo?.ruolo === 'Admin')) {
    const turnoCompleto = {
      ...updatedTurno,
      oraPrevInizio: null,
      oraPrevFine: null,
      oraConsInizio: null,
      oraConsFine: null
    };
    updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(turnoCompleto)
    });
  }

  // TIPO_ASSENZA_PREIMPOSTA_ORARI (MALATTIA, FERIE, ecc.): persisti sempre oraPrevInizio/oraPrevFine
  // calcolati dalle ore contrattuali (00:00 → ore contratto), sovrascrivendo qualsiasi valore precedente
  if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(editData.tipoOrario) && dipendenteInfo) {
    const orarioPerPrev = orari.find(o => o.id === turnoId);
    if (orarioPerPrev) {
      const dayOfWeekPrev = dayjs(orarioPerPrev.data).day();
      let contractHoursPrev = 0;
      switch(dayOfWeekPrev) {
        case 1: contractHoursPrev = dipendenteInfo.lunedi; break;
        case 2: contractHoursPrev = dipendenteInfo.martedi; break;
        case 3: contractHoursPrev = dipendenteInfo.mercoledi; break;
        case 4: contractHoursPrev = dipendenteInfo.giovedi; break;
        case 5: contractHoursPrev = dipendenteInfo.venerdi; break;
        case 6: contractHoursPrev = dipendenteInfo.sabato; break;
        case 0: contractHoursPrev = dipendenteInfo.domenica; break;
      }
      const newOraPrevInizio = '00:00:00';
      const newOraPrevFine = contractHoursPrev > 0
        ? dayjs().hour(0).minute(0).add(contractHoursPrev, 'hour').format('HH:mm:ss')
        : '00:00:00';
      const turnoConPrev = { ...updatedTurno, oraPrevInizio: newOraPrevInizio, oraPrevFine: newOraPrevFine };
      if (isLocked) throw new Error(LOCK_MESSAGE);
      updatedTurno = await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(turnoConPrev)
      });
    }
  }

  // Stabilisce il tipo orario da mostrare: non sostituire con 'ASSENZA'
  const serverTipo = (updatedTurno as any).tipoOrario || (updatedTurno as any).codiceTipoOrario;
  const clientTipo = editData.tipoOrario;
  const safeTipoOrario = serverTipo && serverTipo !== 'ASSENZA' ? serverTipo : clientTipo;

        // Aggiorna lo stato con i valori restituiti dal backend (incluso calcolo automatico)
        setOrari(prevOrari => prevOrari.map(orario => (
          orario.id === turnoId
            ? {
                ...orario,
                oraConsInizio: updatedTurno.oraConsInizio ? (updatedTurno.oraConsInizio.length > 5 ? updatedTurno.oraConsInizio.substring(0,5) : updatedTurno.oraConsInizio) : null,
                oraConsFine: updatedTurno.oraConsFine ? (updatedTurno.oraConsFine.length > 5 ? updatedTurno.oraConsFine.substring(0,5) : updatedTurno.oraConsFine) : null,
                oraPrevInizio: updatedTurno.oraPrevInizio ? (updatedTurno.oraPrevInizio.length > 5 ? updatedTurno.oraPrevInizio.substring(0,5) : updatedTurno.oraPrevInizio) : null,
                oraPrevFine: updatedTurno.oraPrevFine ? (updatedTurno.oraPrevFine.length > 5 ? updatedTurno.oraPrevFine.substring(0,5) : updatedTurno.oraPrevFine) : null,
                codiceTipoOrario: safeTipoOrario,
                tipoOrario: safeTipoOrario,
                annotazioni: updatedTurno.annotazioni || editData.annotazioni,
                turno: updatedTurno.turno || turnoParam
              }
            : orario
        )));
      } else {
        // CREATE: Nuova riga fittizia, crea un nuovo record
        const orario = orari.find(o => o.id === turnoId);
        if (!orario) {
          alert('Errore: impossibile trovare i dati della riga');
          return;
        }

        // Prepara i dati base per POST
        const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario);
        // Per i tipi assenza (incluso LEGGE 104 PERMESSO), forza sempre turno='ASSENZA'
        const turnoField = editData.tipoOrario && isAssenzaTipoFiltered(editData.tipoOrario) ? 'ASSENZA' : tipoTurnoAutomatico;
        
        // Per CHIUSO DITTA, imposta anche gli orari previsti uguali ai consuntivi
        // Per LEGGE 104 PERMESSO, calcola oraPrevInizio/oraPrevFine dalle ore contrattuali
        let oraPrevInizioValue = null;
        let oraPrevFineValue = null;
        if (editData.tipoOrario === 'CHIUSO DITTA' && editData.oraConsInizio && editData.oraConsFine) {
          oraPrevInizioValue = editData.oraConsInizio;
          oraPrevFineValue = editData.oraConsFine;
        } else if (editData.tipoOrario === 'LEGGE 104 PERMESSO' && dipendenteInfo) {
          const dayOfWeek = dayjs(orario.data).day();
          let contractHours = 0;
          switch(dayOfWeek) {
            case 1: contractHours = dipendenteInfo.lunedi; break;
            case 2: contractHours = dipendenteInfo.martedi; break;
            case 3: contractHours = dipendenteInfo.mercoledi; break;
            case 4: contractHours = dipendenteInfo.giovedi; break;
            case 5: contractHours = dipendenteInfo.venerdi; break;
            case 6: contractHours = dipendenteInfo.sabato; break;
            case 0: contractHours = dipendenteInfo.domenica; break;
          }
          if (contractHours > 0) {
            const start = dayjs().hour(0).minute(0);
            const end = start.add(contractHours, 'hour');
            oraPrevInizioValue = start.format('HH:mm');
            oraPrevFineValue = end.format('HH:mm');
          } else {
            oraPrevInizioValue = '00:00';
            oraPrevFineValue = '00:00';
          }
        }
        
        const newTurnoData = {
          neg: userInfo.negozio || '',
          descrizione: userInfo.nome || '',
          data: orario.data,
          oraConsInizio: editData.tipoOrario === 'RIPOSO' ? null : (isCodiceTipoOrarioSpeciale(editData.tipoOrario) && !shouldSendConsuntivoTimes(editData.tipoOrario, editData.oraConsInizio, editData.oraConsFine) ? null : (editData.oraConsInizio || null)),
          oraConsFine: editData.tipoOrario === 'RIPOSO' ? null : (isCodiceTipoOrarioSpeciale(editData.tipoOrario) && !shouldSendConsuntivoTimes(editData.tipoOrario, editData.oraConsInizio, editData.oraConsFine) ? null : (editData.oraConsFine || null)),
          oraPrevInizio: oraPrevInizioValue,
          oraPrevFine: oraPrevFineValue,
          giornoSettimana: orario.giornoSettimana,
          turno: turnoField,
          utenteModifica: userInfo.nome || '',
          codiceTipoOrario: editData.tipoOrario || null,
          annotazioni: editData.annotazioni || null
        };

  if (isLocked) throw new Error(LOCK_MESSAGE);
  const created = await apiRequest<TurnoCreato>('/orari-generati', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newTurnoData)
        });

        // Se codice speciale (esclusi ROL e LEGGE 104 PERMESSO già completi nel POST),
        // trigger auto-calcolo con PUT consuntivo per impostare gli orari previsti automatici
        let createdUpdated = created;
        if (created && isCodiceTipoOrarioSpeciale(editData.tipoOrario) && editData.tipoOrario !== 'LEGGE 104 PERMESSO' && editData.tipoOrario !== 'ROL') {
          const afterParams = new URLSearchParams();
          afterParams.append('codiceTipoOrario', editData.tipoOrario);
          if (editData.annotazioni) afterParams.append('annotazioni', editData.annotazioni);
          afterParams.append('turno', turnoField);

          if (isLocked) throw new Error(LOCK_MESSAGE);
          createdUpdated = await apiRequest<TurnoCreato>(`/orari-generati/${created.id}/consuntivo?${afterParams.toString()}`, { method: 'PUT' });
        }

  // Stabilisce il tipo orario da mostrare per la riga creata: non sostituire con 'ASSENZA'
  const serverTipoCreated = (createdUpdated as any).tipoOrario || (createdUpdated as any).codiceTipoOrario;
  const clientTipoCreated = editData.tipoOrario;
  const safeTipoOrarioCreated = serverTipoCreated && serverTipoCreated !== 'ASSENZA' ? serverTipoCreated : clientTipoCreated;

  // Aggiorna la riga fittizia con l'oggetto definitivo
        setOrari(prevOrari => prevOrari.map(o => (
          o.id === turnoId
            ? {
                ...o,
                id: createdUpdated.id || created.id,
                oraConsInizio: createdUpdated.oraConsInizio ? (createdUpdated.oraConsInizio.length > 5 ? createdUpdated.oraConsInizio.substring(0,5) : createdUpdated.oraConsInizio) : null,
                oraConsFine: createdUpdated.oraConsFine ? (createdUpdated.oraConsFine.length > 5 ? createdUpdated.oraConsFine.substring(0,5) : createdUpdated.oraConsFine) : null,
    codiceTipoOrario: safeTipoOrarioCreated,
    tipoOrario: safeTipoOrarioCreated,
                annotazioni: createdUpdated.annotazioni || editData.annotazioni || undefined,
                turno: createdUpdated.turno || tipoTurnoAutomatico
              } as TurnoCreato
            : o
        )));
      }
      
      // Rimuovi dalla modalità editing - aggiornamento già locale alla riga
      setEditingRows(prev => {
        const newEditingRows = { ...prev };
        delete newEditingRows[turnoId];
        return newEditingRows;
      });
      setShowActionsForRow(null);
      
      // Aggiorna le statistiche dopo il salvataggio
      await refreshAfterTurnoWrite();

    } catch (err) {
      console.error('Error saving consuntivo:', err);
      alert(getApiErrorMessage(err, 'Errore nel salvataggio'));
    }
  };

  const cancelEditing = (turnoId: number) => {
    setEditingRows(prev => {
      const newEditingRows = { ...prev };
      delete newEditingRows[turnoId];
      return newEditingRows;
    });
    setShowActionsForRow(null);
  };

  // Nuova funzione per cancellare i dati consuntivi
  const clearConsuntivo = async (turnoId: number) => {
    try {
  // Evita il loader globale per non causare flicker dell'intera pagina

      // Trova l'orario corrente per capire se esistono orari previsti
      const orarioCorrente = orari.find(o => o.id === turnoId);

      if (orarioCorrente && !orarioCorrente.oraPrevInizio && !orarioCorrente.oraPrevFine) {
        // Nessun orario previsto: elimina il record backend MA mantieni il giorno come riga placeholder
  if (isLocked) throw new Error(LOCK_MESSAGE);
  await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
    setOrari(prev => prev.map(o => {
          if (o.id === turnoId) {
      const placeholderId = -parseInt(dayjs(o.data).format('YYYYMMDD'));
            return {
              id: placeholderId,
              neg: userInfo.negozio || '',
              descrizione: userInfo.nome || '',
              data: o.data,
              oraConsInizio: null,
              oraConsFine: null,
              oraPrevInizio: null,
              oraPrevFine: null,
              giornoSettimana: o.giornoSettimana || dayjs(o.data).format('dddd'),
              turno: '',
              utenteModifica: '',
              dataModifica: '',
              blocco: false,
              outOfMonth: o.outOfMonth
            } as TurnoCreato;
          }
          return o;
        }));
      } else {
        // Orari previsti presenti: cancella solo consuntivo + annotazioni + codice tipo orario
  const params = new URLSearchParams();
  params.append('oraConsInizio', '');
  params.append('oraConsFine', '');
  params.append('annotazioni', '');

  // Calcola il turno in base all'orario previsto (oraPrevInizio)
  const turnoCalcolato = orarioCorrente ? determinaTipoTurno(orarioCorrente.oraPrevInizio, 'LAVORATO') : 'LAVORATO';
  params.append('turno', turnoCalcolato);
  params.append('codiceTipoOrario', 'LAVORATO');

  if (isLocked) throw new Error(LOCK_MESSAGE);
  await apiRequest<TurnoCreato>(`/orari-generati/${turnoId}/consuntivo?${params}`, { method: 'PUT' });

        setOrari(prevOrari => prevOrari.map(orario => (
          orario.id === turnoId
            ? ({
                ...orario,
                oraConsInizio: null,
                oraConsFine: null,
                codiceTipoOrario: 'LAVORATO',
                annotazioni: undefined,
                turno: turnoCalcolato
              } as TurnoCreato)
            : orario
        )));
      }

      // Esci dalla modalità editing
      setEditingRows(prev => {
        const newEditingRows = { ...prev };
        delete newEditingRows[turnoId];
        return newEditingRows;
      });
      setShowActionsForRow(null);

      // Aggiorna le statistiche dopo la cancellazione
      await refreshAfterTurnoWrite();

    } catch (err) {
      console.error('Errore nella cancellazione', err);
      alert('Errore nella cancellazione');
    } finally {
      // niente
    }
  };

  // Funzione per decidere se cancellare i dati o solo annullare l'editing
  const handleCancelOrClear = async (turnoId: number, orario: TurnoCreato) => {
    // Caso speciale: se è una riga duplicata (__RIGA_DUPLICATA__), "Cancella il turno" deve comportarsi come "Cancella riga aggiunta"
    if (orario.annotazioni === '__RIGA_DUPLICATA__') {
      const confirmDel = window.confirm('Vuoi cancellare definitivamente questa riga aggiunta?');
      if (confirmDel) {
        await deleteRow(orario);
      } else {
        cancelEditing(turnoId);
      }
      return;
    }
    // Se ci sono già dei dati consuntivi salvati nel database, chiedi conferma per cancellarli
    // Escludi il marcatore __RIGA_DUPLICATA__ dal controllo
    const hasRealData = orario.oraConsInizio || orario.oraConsFine || orario.tipoOrario || 
                       (orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__');
    
    if (hasRealData) {
      const confirm = window.confirm(
        'Vuoi cancellare definitivamente gli orari consuntivi inseriti?\n\n' +
        'ATTENZIONE: Questa azione cancellerà i dati salvati.'
      );
      
      if (confirm) {
        await clearConsuntivo(turnoId);
      } else {
        // Solo annulla l'editing senza cancellare i dati
        cancelEditing(turnoId);
      }
    } else {
      // Se non ci sono dati salvati, semplicemente annulla l'editing
      cancelEditing(turnoId);
    }
  };

  // Ref per tracciare editingRows senza dipendenze nell'effect
  const editingRowsRef = useRef(editingRows);
  useEffect(() => {
    editingRowsRef.current = editingRows;
  }, [editingRows]);

  // Effect per gestire il click fuori dalla riga in modifica
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const editingIds = Object.keys(editingRowsRef.current);
      if (editingIds.length === 0) return;

      const target = event.target as HTMLElement;
      
      // Per ogni riga in modifica
      editingIds.forEach(idStr => {
        const id = parseInt(idStr);
        const editData = editingRowsRef.current[id];
        
        // Cerca l'elemento della riga
        const rowElement = document.querySelector(`tr[data-row-id="${id}"]`);
        
        // Verifica se il click è su un elemento del menu a tendina (MUI Portal) o altri elementi di input fluttuanti
        const isMenuClick = target.closest('.MuiMenu-paper') || 
                          target.closest('.MuiPopover-paper') || 
                          target.closest('.MuiMenuItem-root') ||
                          target.closest('.MuiList-root') ||
                          target.closest('[role="listbox"]');

        // Se il click è fuori dalla riga E non è nel menu a tendina
        if (rowElement && !rowElement.contains(target) && !isMenuClick) {
          // Controlla se i dati minimi sono presenti
          if (editData && editData.oraConsInizio && editData.oraConsFine && editData.tipoOrario) {
            // Salva
            saveConsuntivoAuto(id, editData).then((success) => {
              if (success) {
                // Rimuovi la riga dallo stato di editing solo se il salvataggio ha avuto successo
                setEditingRows(prev => {
                  const newState = { ...prev };
                  delete newState[id];
                  return newState;
                });
                // Se era l'unica riga, resetta anche showActionsForRow
                setShowActionsForRow(prev => prev === id ? null : prev);
              }
            });
          } else {
            // Annulla
            cancelEditing(id);
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [saveConsuntivoAuto]); // cancelEditing è stabile o definita nello scope

  const updateEditingValue = async (turnoId: number, field: 'oraConsInizio' | 'oraConsFine' | 'tipoOrario' | 'annotazioni', value: string) => {
    // Aggiorna il valore normalmente
    const newEditingData = {
      ...editingRows[turnoId],
      [field]: value
    };

    // Se il campo modificato è il tipo orario e è uno dei codici speciali
    if (field === 'tipoOrario') {
      // Imposta showActionsForRow per rendere visibili i pulsanti Salva/Annulla
      // Questo è necessario per far funzionare l'auto-save quando si clicca fuori dalla riga
      setShowActionsForRow(turnoId);
      // Se Admin imposta RIPOSO, azzera tutti gli orari (previsti e consuntivi)
      if (value === 'RIPOSO' && (viewerInfo?.ruolo === 'Admin' || userInfo?.ruolo === 'Admin')) {
        newEditingData.oraConsInizio = '' as any;
        newEditingData.oraConsFine = '' as any;
        
        // Aggiorna anche gli orari previsti a null nell'orario corrente
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario) {
          setOrari(prevOrari => prevOrari.map(o => 
            o.id === turnoId 
              ? { ...o, oraPrevInizio: null, oraPrevFine: null }
              : o
          ));
        }
      } else if (isCodiceTipoOrarioSpeciale(value) && !TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(value)) {
        // Pulisci i campi orari SOLO per i codici speciali che NON sono in TIPO_ASSENZA_PREIMPOSTA_ORARI
        // I tipi in TIPO_ASSENZA_PREIMPOSTA_ORARI (FERIE, MALATTIA, ecc.) avranno gli orari compilati automaticamente più avanti
        newEditingData.oraConsInizio = '' as any;
        newEditingData.oraConsFine = '' as any;
      }

      // LOGICA LEGGE 104 PERMESSO: Imposta SEMPRE 00:00 -> ore contrattuali SOLO per Ora_Prev (NON Cons)
      if (value === 'LEGGE 104 PERMESSO' && dipendenteInfo) {
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipendenteInfo.lunedi; break;
              case 2: contractHours = dipendenteInfo.martedi; break;
              case 3: contractHours = dipendenteInfo.mercoledi; break;
              case 4: contractHours = dipendenteInfo.giovedi; break;
              case 5: contractHours = dipendenteInfo.venerdi; break;
              case 6: contractHours = dipendenteInfo.sabato; break;
              case 0: contractHours = dipendenteInfo.domenica; break;
            }

            // NON impostare orari consuntivi - lasciali vuoti
            newEditingData.oraConsInizio = '' as any;
            newEditingData.oraConsFine = '' as any;
            
            // IMPORTANTE: Imposta SOLO Ora_Prev (sempre per LEGGE 104 PERMESSO)
            if (contractHours > 0) {
                const start = dayjs().hour(0).minute(0);
                const end = start.add(contractHours, 'hour');
                // Aggiorna direttamente l'orario in locale per mostrare subito gli orari previsti
                setOrari(prevOrari => prevOrari.map(o => 
                    o.id === turnoId 
                        ? { ...o, oraPrevInizio: start.format('HH:mm:ss'), oraPrevFine: end.format('HH:mm:ss') }
                        : o
                ));
            } else {
                setOrari(prevOrari => prevOrari.map(o => 
                    o.id === turnoId 
                        ? { ...o, oraPrevInizio: '00:00:00', oraPrevFine: '00:00:00' }
                        : o
                ));
            }
        }
      }

      // LOGICA ASSENZE CON CONSUNTIVO (ROL, AVIS, ecc. - esclusa LEGGE 104 già gestita sopra): 
      // Se selezionato uno di questi codici, controlla se esiste un turno LAVORATO nello stesso giorno
      // e se le ore lavorate sono inferiori a quelle contrattuali, riempi automaticamente con la differenza
      const CODICI_AUTOCOMPILA_CONSUNTIVO = ['ROL', 'AVIS', 'LUTTO', 'PERMESSI PARENTALI', 'PERMESSI ELETTORALI', 'PERMESSI STUDIO', 'LEGGE 118 PERMESSO'];
      if (CODICI_AUTOCOMPILA_CONSUNTIVO.includes(value) && dipendenteInfo) {
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipendenteInfo.lunedi; break;
              case 2: contractHours = dipendenteInfo.martedi; break;
              case 3: contractHours = dipendenteInfo.mercoledi; break;
              case 4: contractHours = dipendenteInfo.giovedi; break;
              case 5: contractHours = dipendenteInfo.venerdi; break;
              case 6: contractHours = dipendenteInfo.sabato; break;
              case 0: contractHours = dipendenteInfo.domenica; break;
            }

            // DEFAULT SINGLE ROW: 00:00 -> 00:00 + contractHours
            if (contractHours > 0) {
                 const start = dayjs().hour(0).minute(0);
                 const end = start.add(contractHours, 'hour');
                 newEditingData.oraConsInizio = start.format('HH:mm');
                 newEditingData.oraConsFine = end.format('HH:mm');
            }

          // Cerca un altro turno LAVORATO nello stesso giorno
          const lavoratoShift = orari.find(o => 
            o.data === currentOrario.data && 
            o.id !== turnoId && 
            (o.tipoOrario === 'LAVORATO' || o.codiceTipoOrario === 'LAVORATO') &&
            o.oraConsInizio && o.oraConsFine
          );

          if (lavoratoShift && lavoratoShift.oraConsInizio && lavoratoShift.oraConsFine) {
            // Calcola ore lavorate
            const start = dayjs(`2000-01-01 ${lavoratoShift.oraConsInizio}`);
            const end = dayjs(`2000-01-01 ${lavoratoShift.oraConsFine}`);
            const workedMinutes = end.diff(start, 'minute');
            const workedHours = workedMinutes / 60;

            // Se ore lavorate < ore contrattuali, riempi la differenza
            if (contractHours > workedHours) {
              const remainingHours = contractHours - workedHours;
              // Nuovo Inizio = Fine del turno lavorato
              const newStart = end; 
              // Nuova Fine = Nuovo Inizio + ore mancanti
              const newEnd = newStart.add(remainingHours, 'hour');
              
              newEditingData.oraConsInizio = newStart.format('HH:mm');
              newEditingData.oraConsFine = newEnd.format('HH:mm');
            }
          }
        }
      }

      // LOGICA CHIUSO DITTA: Imposta le ore consuntive in base alle ore contrattuali
      if (value === 'CHIUSO DITTA' && dipendenteInfo) {
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipendenteInfo.lunedi; break;
              case 2: contractHours = dipendenteInfo.martedi; break;
              case 3: contractHours = dipendenteInfo.mercoledi; break;
              case 4: contractHours = dipendenteInfo.giovedi; break;
              case 5: contractHours = dipendenteInfo.venerdi; break;
              case 6: contractHours = dipendenteInfo.sabato; break;
              case 0: contractHours = dipendenteInfo.domenica; break;
            }

            // Imposta 00:00 -> 00:00 + contractHours
            if (contractHours > 0) {
                 const start = dayjs().hour(0).minute(0);
                 const end = start.add(contractHours, 'hour');
                 newEditingData.oraConsInizio = start.format('HH:mm');
                 newEditingData.oraConsFine = end.format('HH:mm');
            } else {
                 // Se non ci sono ore contrattuali, imposta 00:00 - 00:00
                 newEditingData.oraConsInizio = '00:00';
                 newEditingData.oraConsFine = '00:00';
            }
            
            // IMPORTANTE: Imposta anche Ora_Prev per righe vuote o senza orari previsti
            // In questo modo quando si aggiunge CHIUSO DITTA su una riga vuota vengono compilati anche gli orari previsti
            if (!currentOrario.oraPrevInizio || !currentOrario.oraPrevFine || currentOrario.turno === 'turnoVuoto' || !currentOrario.turno) {
                // Copia gli orari consuntivi anche negli orari previsti
                if (contractHours > 0) {
                    const start = dayjs().hour(0).minute(0);
                    const end = start.add(contractHours, 'hour');
                    // Aggiorna direttamente l'orario in locale per mostrare subito gli orari previsti
                    setOrari(prevOrari => prevOrari.map(o => 
                        o.id === turnoId 
                            ? { ...o, oraPrevInizio: start.format('HH:mm:ss'), oraPrevFine: end.format('HH:mm:ss') }
                            : o
                    ));
                } else {
                    setOrari(prevOrari => prevOrari.map(o => 
                        o.id === turnoId 
                            ? { ...o, oraPrevInizio: '00:00:00', oraPrevFine: '00:00:00' }
                            : o
                    ));
                }
            }
        }
      }

      // LOGICA TIPO_ASSENZA_PREIMPOSTA_ORARI (FERIE, MALATTIA, LUTTO, MATERNITÀ, AVIS, CONGEDO MATRIMONIALE, ASPETTATIVA, PERMESSI ELETTORALI):
      // Imposta le ore consuntive e previste in base alle ore contrattuali
      if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(value) && dipendenteInfo) {
        const currentOrario = orari.find(o => o.id === turnoId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipendenteInfo.lunedi; break;
              case 2: contractHours = dipendenteInfo.martedi; break;
              case 3: contractHours = dipendenteInfo.mercoledi; break;
              case 4: contractHours = dipendenteInfo.giovedi; break;
              case 5: contractHours = dipendenteInfo.venerdi; break;
              case 6: contractHours = dipendenteInfo.sabato; break;
              case 0: contractHours = dipendenteInfo.domenica; break;
            }

            // Imposta 00:00 -> 00:00 + contractHours
            if (contractHours > 0) {
                 const start = dayjs().hour(0).minute(0);
                 const end = start.add(contractHours, 'hour');
                 newEditingData.oraConsInizio = start.format('HH:mm');
                 newEditingData.oraConsFine = end.format('HH:mm');
            } else {
                 // Se non ci sono ore contrattuali, imposta 00:00 - 00:00
                 newEditingData.oraConsInizio = '00:00';
                 newEditingData.oraConsFine = '00:00';
            }
            
            // IMPORTANTE: Se il turno esiste già (turnoId > 0), aggiorna SEMPRE gli orari previsti nel backend
            // quando si seleziona MALATTIA (o qualsiasi TIPO_ASSENZA_PREIMPOSTA_ORARI)
            if (turnoId > 0) {
                // Aggiorna gli orari previsti nel backend
                const oraPrevInizio = '00:00:00';
                const oraPrevFine = contractHours > 0 ? dayjs().hour(0).minute(0).add(contractHours, 'hour').format('HH:mm:ss') : '00:00:00';
                
                const turnoCompleto = {
                    ...currentOrario,
                    oraPrevInizio: oraPrevInizio,
                    oraPrevFine: oraPrevFine
                };
                
                // Esegui l'UPDATE del turno nel backend
                apiRequest<TurnoCreato>(`/orari-generati/${turnoId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(turnoCompleto)
                }).then(updatedTurno => {
                    // Aggiorna lo stato locale con i dati dal backend
                    setOrari(prevOrari => prevOrari.map(o => 
                        o.id === turnoId 
                            ? { ...o, oraPrevInizio: updatedTurno.oraPrevInizio, oraPrevFine: updatedTurno.oraPrevFine }
                            : o
                    ));
                }).catch(err => {
                    console.error(`Errore nell'aggiornamento degli orari previsti per ${value}:`, err);
                });
                // Aggiorna subito lo stato locale (prima della risposta async)
                setOrari(prevOrari => prevOrari.map(o => 
                    o.id === turnoId 
                        ? { ...o, oraPrevInizio: oraPrevInizio, oraPrevFine: oraPrevFine }
                        : o
                ));
            } else if (turnoId < 0) {
                // Per righe nuove (placeholder negativi), aggiorna solo lo stato locale
                if (contractHours > 0) {
                    const start = dayjs().hour(0).minute(0);
                    const end = start.add(contractHours, 'hour');
                    setOrari(prevOrari => prevOrari.map(o => 
                        o.id === turnoId 
                            ? { ...o, oraPrevInizio: start.format('HH:mm:ss'), oraPrevFine: end.format('HH:mm:ss') }
                            : o
                    ));
                } else {
                    setOrari(prevOrari => prevOrari.map(o => 
                        o.id === turnoId 
                            ? { ...o, oraPrevInizio: '00:00:00', oraPrevFine: '00:00:00' }
                            : o
                    ));
                }
            }
        }
      }

      // Se selezionata un'assenza, il Turno sarà ASSENZA al salvataggio
      // Non serve modificarlo qui nello stato locale della tabella perché viene calcolato lato save
    }

    // VALIDAZIONE ORA_CONS_INIZIO / ORA_CONS_FINE per ROL: l'intervallo ROL non deve sovrapporsi con turni esistenti dello stesso giorno
    if ((field === 'oraConsInizio' || field === 'oraConsFine') && newEditingData.tipoOrario === 'ROL' && value) {
      const currentOrario = orari.find(o => o.id === turnoId);
      if (currentOrario) {
        const rolStart = field === 'oraConsInizio' ? value : newEditingData.oraConsInizio;
        const rolEnd   = field === 'oraConsFine'   ? value : newEditingData.oraConsFine;
        if (rolStart && rolEnd) {
          const rS = dayjs(`2000-01-01 ${rolStart}`);
          const rE = dayjs(`2000-01-01 ${rolEnd}`);
          const conflictShift = orari.find(o =>
            o.data === currentOrario.data &&
            o.id !== turnoId &&
            o.oraConsInizio && o.oraConsFine &&
            rS < dayjs(`2000-01-01 ${o.oraConsFine}`) &&
            dayjs(`2000-01-01 ${o.oraConsInizio}`) < rE
          );
          if (conflictShift) {
            const consInizio = conflictShift.oraConsInizio?.substring(0, 5);
            const consFine = conflictShift.oraConsFine?.substring(0, 5);
            const fieldLabel = field === 'oraConsInizio' ? 'Ora Cons. Inizio' : 'Ora Cons. Fine';
            alert(`${fieldLabel} non valida: l'intervallo ROL ${rolStart.substring(0, 5)} → ${rolEnd.substring(0, 5)} si sovrappone al turno ${consInizio} → ${consFine} dello stesso giorno.`);
            return;
          }
        } else {
          // Solo un estremo noto: verifica puntuale sull'estremo inserito
          const inputTime = dayjs(`2000-01-01 ${value}`);
          const conflictShift = orari.find(o =>
            o.data === currentOrario.data &&
            o.id !== turnoId &&
            o.oraConsInizio && o.oraConsFine &&
            inputTime > dayjs(`2000-01-01 ${o.oraConsInizio}`) &&
            inputTime < dayjs(`2000-01-01 ${o.oraConsFine}`)
          );
          if (conflictShift) {
            const consInizio = conflictShift.oraConsInizio?.substring(0, 5);
            const consFine = conflictShift.oraConsFine?.substring(0, 5);
            const fieldLabel = field === 'oraConsInizio' ? 'Ora Cons. Inizio' : 'Ora Cons. Fine';
            alert(`${fieldLabel} non valida: l'orario ${value.substring(0, 5)} ricade nell'intervallo ${consInizio} → ${consFine} di un turno già presente in questo giorno.`);
            return;
          }
        }
      }
    }

    // VALIDAZIONE ORA_CONS_FINE per RIGA_DUPLICATA ROL (admin): oraConsFine - oraConsInizio <= residuo contrattuale
    if (field === 'oraConsFine' && isAdminViewer && dipendenteInfo) {
      const currentOrario = orari.find(o => o.id === turnoId);
      if (currentOrario && currentOrario.annotazioni === '__RIGA_DUPLICATA__') {
        const currentEditing = editingRows[turnoId];
        if (currentEditing && currentEditing.tipoOrario === 'ROL') {
          // Ore contrattuali per il giorno
          const dayOfWeek = dayjs(currentOrario.data).day();
          let contractHours = 0;
          switch(dayOfWeek) {
            case 1: contractHours = dipendenteInfo.lunedi; break;
            case 2: contractHours = dipendenteInfo.martedi; break;
            case 3: contractHours = dipendenteInfo.mercoledi; break;
            case 4: contractHours = dipendenteInfo.giovedi; break;
            case 5: contractHours = dipendenteInfo.venerdi; break;
            case 6: contractHours = dipendenteInfo.sabato; break;
            case 0: contractHours = dipendenteInfo.domenica; break;
          }
          // Trova il turno LAVORATO nello stesso giorno
          const lavoratoShift = orari.find(o =>
            o.data === currentOrario.data &&
            o.id !== turnoId &&
            (o.tipoOrario === 'LAVORATO' || o.codiceTipoOrario === 'LAVORATO') &&
            o.oraConsInizio && o.oraConsFine
          );
          let residualHours = contractHours;
          if (lavoratoShift && lavoratoShift.oraConsInizio && lavoratoShift.oraConsFine) {
            const lStart = dayjs(`2000-01-01 ${lavoratoShift.oraConsInizio}`);
            const lEnd = dayjs(`2000-01-01 ${lavoratoShift.oraConsFine}`);
            const workedMinutes = lEnd.diff(lStart, 'minute');
            residualHours = contractHours - workedMinutes / 60;
          }
          // Calcola le ore di ROL inserite
          const oraConsInizio = newEditingData.oraConsInizio || currentEditing.oraConsInizio;
          if (oraConsInizio && value) {
            const rolStart = dayjs(`2000-01-01 ${oraConsInizio}`);
            const rolEnd = dayjs(`2000-01-01 ${value}`);
            const rolMinutes = rolEnd.diff(rolStart, 'minute');
            const rolHours = rolMinutes / 60;
            if (rolHours > residualHours + 0.001) {
              const residualFormatted = `${Math.floor(residualHours)}h ${Math.round((residualHours % 1) * 60)}m`;
              alert(`Ora_Cons_Fine non valida: le ore ROL (${rolHours.toFixed(2)}h) supererebbero il residuo contrattuale (${residualFormatted}). Imposta un orario inferiore.`);
              return; // Non aggiornare lo stato
            }
          }
        }
      }
    }

    setEditingRows(prev => ({
      ...prev,
      [turnoId]: newEditingData
    }));
  };

  const duplicateRow = async (originalOrario: TurnoCreato) => {
    // Controlla se la data può essere modificata
    if (!canEditOrario(originalOrario.data)) {
      return; // Non permettere la duplicazione se la data non è modificabile
    }

    try {
      // Crea un nuovo turno per lo stesso giorno
      const newTurno = {
        neg: originalOrario.neg,
        descrizione: originalOrario.descrizione,
        data: originalOrario.data,
        oraPrevInizio: null,
        oraPrevFine: null,
        giornoSettimana: originalOrario.giornoSettimana,
        turno: '', // Turno vuoto, verrà calcolato automaticamente quando si compila
        tipoOrario: 'LAVORATO',
        codiceTipoOrario: 'LAVORATO',
        annotazioni: '__RIGA_DUPLICATA__', // Identificativo nascosto per le righe duplicate
        utenteModifica: userInfo.nome || 'Sistema'
      };

      // Chiama l'API per creare il nuovo turno
      const created = await apiRequest<TurnoCreato>('/orari-generati', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTurno)
      });

      // Inserisci localmente la nuova riga subito dopo l'originale, evitando flicker
      setOrari(prev => {
        const idx = prev.findIndex(o => o.id === originalOrario.id);
        const normalized: TurnoCreato = {
          ...created,
          // Normalizza formato HH:mm
          oraConsInizio: created.oraConsInizio ? (created.oraConsInizio.length > 5 ? created.oraConsInizio.substring(0,5) : created.oraConsInizio) : null,
          oraConsFine: created.oraConsFine ? (created.oraConsFine.length > 5 ? created.oraConsFine.substring(0,5) : created.oraConsFine) : null,
          giornoSettimana: created.giornoSettimana || originalOrario.giornoSettimana || dayjs(created.data).format('dddd'),
          outOfMonth: originalOrario.outOfMonth,
          tipoOrario: created.tipoOrario || 'LAVORATO',
          codiceTipoOrario: created.codiceTipoOrario || 'LAVORATO'
        } as TurnoCreato;
        const next = idx === -1 ? [...prev, normalized] : [...prev.slice(0, idx + 1), normalized, ...prev.slice(idx + 1)];
        return sortOrariByDate(next);
      });

      // Aggiorna le statistiche dopo la duplicazione
      await refreshAfterTurnoWrite();

    } catch (err) {
      console.error('Error duplicating row:', err);
      alert('Errore nella duplicazione della riga');
    }
  };

  const deleteRow = async (orario: TurnoCreato) => {
    try {
      // Caso 0: Riga generata automaticamente (fittizia) - non dovrebbe succedere
      if (orario.id < 0) {
        alert('Non puoi cancellare una riga fittizia. Usa il pulsante + per aggiungere una riga reale.');
        return;
      }

      // Solo righe aggiunte dall'utente possono essere cancellate con questo pulsante
      if (orario.annotazioni === '__RIGA_DUPLICATA__') {
        // Conferma la cancellazione
        if (!window.confirm('Sei sicuro di voler cancellare questa riga?')) {
          return;
        }

        // Chiama l'API per cancellare completamente il turno
        await apiRequest(`/orari-generati/${orario.id}`, {
          method: 'DELETE'
        });
      } else {
        // Non dovrebbe mai arrivare qui, ma per sicurezza
        alert('Puoi cancellare solo le righe aggiunte, non quelle originali');
        return;
      }

      // Rimuovi localmente la riga senza ricaricare tutto
      setOrari(prev => sortOrariByDate(prev.filter(o => o.id !== orario.id)));

      // Aggiorna le statistiche dopo la cancellazione
      await refreshAfterTurnoWrite();

    } catch (err) {
      console.error('Error deleting row:', err);
      alert('Errore nella cancellazione della riga');
    }
  };

  if (loading || turniLoading) {
    return (
      <Container>
        <Typography>Caricamento orari...</Typography>
        <InlineLoader rows={6} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
  {isLocked && (<Alert severity="warning" sx={{ mb: 2 }}>{LOCK_MESSAGE}</Alert>)}
      {/* Mostra la data di blocco più restrittiva */}
      {(((!isAdminViewer && !isCoordinatoreViewer && dataBloccoGlobale) || (isCoordinatoreViewer && dataBloccoCoordinatori) || dataControllo)) && (() => {
        // Determina quale data è più restrittiva (maggiore)
        const dataBloccoRuolo = isCoordinatoreViewer ? dataBloccoCoordinatori : dataBloccoGlobale;
        let dataBloccante: dayjs.Dayjs;
        let isHardBlock = false;
        if (dataBloccoRuolo && dataControllo) {
          if (dataBloccoRuolo.isAfter(dataControllo, 'day') || dataBloccoRuolo.isSame(dataControllo, 'day')) {
            dataBloccante = dataBloccoRuolo;
            isHardBlock = true;
          } else {
            dataBloccante = dataControllo;
            isHardBlock = false;
          }
        } else if (dataBloccoRuolo) {
          dataBloccante = dataBloccoRuolo;
          isHardBlock = true;
        } else {
          dataBloccante = dataControllo!;
          isHardBlock = false;
        }
        
        // Verifica se il mese selezionato è successivo alla data di blocco
        const lastDayOfSelectedMonth = selectedDate.endOf('month');
        const isSelectedMonthAfterBlock = lastDayOfSelectedMonth.isAfter(dataBloccante, 'day');
        
        // Mostra l'alert solo se il mese selezionato non è successivo alla data di blocco
        if (isSelectedMonthAfterBlock) {
          return null;
        }
        
        return (
          <Alert severity={isHardBlock ? "error" : "info"} sx={{ mb: 2 }}>
            Modifiche bloccate fino al {dataBloccante.format('DD/MM/YYYY')}
            {!isHardBlock && isAdminViewer && " - Admin può modificare"}
          </Alert>
        );
      })()}
      {!hideHeader && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Schedule fontSize="large" sx={{ mr: 2, color: PAGE_COLOR }} />
            <Typography variant="h4" component="h1">
              Il Mio Orario
            </Typography>
            {/* {showCambiTurno && (
              <Tooltip title="Gestione Cambi Turno">
                <IconButton 
                  onClick={() => setCambiTurnoPopupOpen(true)}
                  sx={{ ml: 2 }}
                  color={cambiTurnoStats?.haRichiestePendenti ? 'warning' : 'default'}
                >
                  {cambiTurnoStats?.haRichiestePendenti ? <ChangeCircle /> : <ChangeCircleOutlined />}
                </IconButton>
              </Tooltip>
            )} */}
          </Box>
        </Box>
      )}
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Calendario fisso a sinistra */}
        <Box sx={{ 
          flex: '0 0 300px',
          position: 'sticky',
          top: 20,
          height: 'fit-content'
        }}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                <StaticDatePicker
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                  onMonthChange={(newValue) => newValue && setSelectedDate(newValue)}
                  views={['month', 'day']}
                  displayStaticWrapperAs="desktop"
                  showDaysOutsideCurrentMonth
                  fixedWeekNumber={6}
                  slotProps={{
                    actionBar: {
                      actions: []
                    },
                    calendarHeader: {
                      format: 'MMMM YYYY'
                    }
                  }}
                />
              </LocalizationProvider>
            </CardContent>
          </Card>
          {/* Contenuto aggiuntivo sotto il calendario */}
          {additionalCalendarContent && (
            <Box sx={{ mt: 2 }}>
              {additionalCalendarContent}
            </Box>
          )}
        </Box>
        
        {/* Contenuto principale - Tabella */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* Informazioni sui periodi preferenze */}
          {isPreferencesPeriod && isTargetMonth && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Inserimento preferenze attivo</strong>
                <br />
                Puoi inserire massimo 2 riposi a settimana per <strong>{selectedDate.format('MMMM YYYY')}</strong>
                <br />
                <span style={{ color: '#1976d2' }}>
                  Clicca sul cuore al fianco della data per inserire una preferenza
                </span>
              </Typography>
            </Alert>
          )}

          {isPreferencesPeriod && !isTargetMonth && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Puoi inserire preferenze solo per il mese di <strong>{calculateTargetMonth().format('MMMM YYYY')}</strong>.
                <br />
                Seleziona il mese indicato per inserire le preferenze.
              </Typography>
            </Alert>
          )}

          {!isPreferencesPeriod && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Le preferenze sono disponibili <strong>dal 16 alla fine del mese corrente </strong>
                per il mese di <strong>{dayjs().add(2, 'month').format('MMMM YYYY')}</strong>.
              </Typography>
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="h2" align="center" sx={{ 
                flex: 1,
                fontWeight: 'bold', 
                backgroundColor: PAGE_COLOR,
                color: 'white',
                               py:  1,
                borderRadius: 1,
                textAlign: 'center'
              }}>
                Orario di {selectedDate.format('MMMM YYYY')}
              </Typography>
              {isAdminViewer && (
                <Tooltip title="Scarica tabella orari (CSV)">
                  <IconButton color="primary" onClick={exportOrariCSV} sx={{ ml: 1 }}>
                    <FileDownload />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Paper sx={{ 
              width: '100%',
              boxShadow: 3,   
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <TableContainer sx={{ 
                width: '100%',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: '4px',
                }
              }}>
                <Table stickyHeader sx={{ 
                  '& .MuiTableCell-root': { 
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    padding: '6px 8px',
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableBody-root .MuiTableRow-root': {
                    height: '52px',
                    minHeight: '52px',
                    maxHeight: '52px'
                  },
                  '& .MuiTableBody-root .MuiTableCell-root': {
                    height: '52px',
                    minHeight: '52px',
                    maxHeight: '52px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    textAlign: 'center',
                    fontSize: '0.8rem'
                  },
                  '& .MuiTableBody-root .time-cell': {
                    height: '32px',
                    minHeight: '32px',
                    padding: '0',
                    justifyContent: 'center',
                    fontSize: '0.8rem'
                  },
                  '& .MuiTableBody-root .MuiTextField-root': {
                    height: '32px',
                    '& .MuiInputBase-root': {
                      height: '32px',
                      fontSize: '0.8rem'
                    },
                    '& .MuiInputBase-input': {
                      padding: '4px 8px',
                      fontSize: '0.8rem',
                      textAlign: 'center'
                    }
                  },
                  '& .MuiTableBody-root .MuiSelect-select': {
                    height: '32px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  width: '100%',
                  tableLayout: 'fixed'
                }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(0, 191, 255, 0.1)' }}>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '70px', fontSize: '0.8rem' }}>Negozio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '90px', fontSize: '0.8rem' }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '80px', fontSize: '0.8rem' }}>Turno</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '70px', fontSize: '0.8rem' }}>Ora prev inizio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '70px', fontSize: '0.8rem' }}>Ora prev fine</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '80px', fontSize: '0.8rem' }}>Conferma</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '90px', fontSize: '0.8rem' }}>Ora cons inizio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '90px', fontSize: '0.8rem' }}>Ora cons fine</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '120px', fontSize: '0.8rem' }}>Tipo orario</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',width: '100px', fontSize: '0.8rem' }}>Annotazioni</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '50px', fontSize: '0.8rem' }}>DUP</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60px', fontSize: '0.8rem' }}>Azioni</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center',backgroundColor: '#f5f5f5', width: '60px', fontSize: '0.8rem' }}>Tot ore</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                {orari.map((orario) => {
                  const isEditing = editingRows[orario.id];
                  const showActions = showActionsForRow === orario.id;
                  const isBlocked = isTurnoBlocked(orario);
                  const canModifyBlocked = canUserModifyBlockedTurno(orario);
                  // Verifica se il dipendente può modificare questo turno in base al tipo orario
                  const role = (userInfo.ruolo || '').toLowerCase();
                  const isDipendente = role === 'dipendente';
                  const isAdminOrCoord = role === 'admin' || role === 'coordinatore';
                  const tipoOrarioTurno = orario.codiceTipoOrario || orario.tipoOrario || null;
                  const canModifyThisTurno = !isDipendente || canDipendenteModifyTipoOrarioFiltered(tipoOrarioTurno, permessiSpeciali);
                  
                  // Verifica se ci sono turni LAVORATO precedenti non confermati (per ruoli con restrizione)
                  const hasPreviousUnconfirmed = isRuoloConRestrizioneConferma(role) && hasPreviousUnconfirmedLavorato(orari, orario.data);
                  // Blocca l'inserimento di OraConsInizio e OraConsFine se ci sono turni precedenti non confermati
                  const canEditConsuntivo = !hasPreviousUnconfirmed;
                  
                  return (
                    <TableRow 
                      key={`${orario.id}-${orario.data}`}
                      data-row-id={orario.id}
                      sx={{
                        // Righe bloccate: colora solo le celle in grigio chiaro mantenendo i bordi visibili
	                        ...(isBlocked && {
	                          '& td': {
	                            backgroundColor: 'rgba(224, 224, 224, 0.6)',
	                            color: '#616161'
	                          }
	                        }),
	                        // Se non può modificare la riga bloccata: blocco completo (non cliccabile)
	                        ...(isBlocked && !canModifyBlocked && {
	                          pointerEvents: 'none'
	                        })
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}><span translate="no">{(orario.neg || '').toUpperCase()}</span></TableCell>
                      <TableCell 
                        sx={{ 
                          backgroundColor: (
                            isBlocked ? 'rgba(224, 224, 224, 0.6)' :
                            isPreferenceTurno(orario) ? '#ffebee' :
                            orario.outOfMonth ? '#fafafa' :
                            isWeekend(orario.data) ? '#f5f5f5' :
                            (canShowPreferences && (!orario.turno || orario.turno === 'turnoVuoto' || isRiposoDay(orario)) ? '#e3f2fd' : 'transparent')
                          )
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.75rem' }}>{formatDataColumn(orario.data, orario.id > 0)}</span>
                          {(isPreferenceTurno(orario) || (canShowPreferences && (!orario.turno || orario.turno === 'turnoVuoto' || isRiposoDay(orario)) && !orario.outOfMonth)) && (
                            <IconButton
                              size="small"
                              disabled={isBlocked && !canModifyBlocked}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPreferenceTurno(orario)) {
                                  handleExistingPreferenceClick(orario);
                                } else {
                                  handlePreferenceClick(orario);
                                }
                              }}
                              sx={{ padding: 0.5 }}
                            >
                              {isPreferenceTurno(orario) ? <Favorite sx={{ color: 'red', fontSize: '1rem' }} /> : <FavoriteBorder sx={{ fontSize: '1rem' }} />}
                            </IconButton>
                          )}
                        </Box>
                        {orario.outOfMonth && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#9e9e9e' }}>
                            Fuori mese
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                          <Typography variant="body2">
                            {(() => {
                              const isEditing = !!editingRows[orario.id];
                              if (isEditing) {
                                const tipoSel = editingRows[orario.id].tipoOrario;
                                if (tipoSel === 'RIPOSO') return 'Riposo';
                                if (isAssenzaTipoFiltered(tipoSel)) return 'ASSENZA';
                              }
                              return orario.turno || (canShowPreferences ? 'Disponibile' : 'Non assegnato');
                            })()}
                          </Typography>
                          {/* Chip negozio temporaneo rimosso per richiesta: mantenuto solo in MioOrarioCel */}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'rgba(255, 235, 59, 0.5)' }}>
                        {orario.codiceTipoOrario === 'CHIUSO DITTA' ? '' : formatTime(orario.oraPrevInizio)}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'rgba(255, 235, 59, 0.5)' }}>
                        {orario.codiceTipoOrario === 'CHIUSO DITTA' ? '' : formatTime(orario.oraPrevFine)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
        {shouldShowCopyButton(orario) && (
                          <Tooltip title={hasPreviousUnconfirmed ? 'Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)' : 'Conferma orario'}>
                            <span>
                            <IconButton 
          onClick={() => copyPrevToConsuntivo(orario.id, orario.oraPrevInizio, orario.oraPrevFine)}
                              size="small"
          disabled={!enableAlwaysConfirm || !canEditConsuntivo}
                            >
                              <CheckCircle sx={{ color: (!enableAlwaysConfirm || !canEditConsuntivo) ? 'grey.400' : '#8bc34a' }} />
                            </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        backgroundColor: getCellBackgroundColor(orario.oraConsInizio, orario.oraPrevInizio),
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)'
                      }}>
                        {isEditing ? (
                          isDisableConsuntivo(isEditing.tipoOrario) ? (
                            <Typography variant="body2" color="text.disabled">
                              {isEditing.tipoOrario === 'CHIUSO DITTA' ? '' : '—'}
                            </Typography>
                          ) : (
                            <TextField
                              type="time"
                              value={isEditing.oraConsInizio}
                              onChange={(e) => updateEditingValue(orario.id, 'oraConsInizio', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, orario.id, 'oraConsInizio')}
                              size="small"
                              disabled={!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(isEditing.tipoOrario) || (isAssenzaConConsuntivo(isEditing.tipoOrario) && isEditing.tipoOrario !== 'ROL' && orari.some(o => o.data === orario.data && o.id !== orario.id && (o.tipoOrario === 'LAVORATO' || o.codiceTipoOrario === 'LAVORATO')))}
                              sx={{ 
                                width: '100%',
                                '& .MuiInputBase-input': { padding: '4px' }
                              }}
                              inputProps={{ 
                                step: 300,
                                'data-field': 'oraConsInizio',
                                'data-row': orario.id
                              }}
                              autoFocus={activeCell?.rowId === orario.id && activeCell?.field === 'oraConsInizio'}
                            />
                          )
                        ) : (
                          <Tooltip title={hasPreviousUnconfirmed ? 'Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)' : ''}>
                          <Box 
                            className="time-cell"
                            onClick={() => canModifyThisTurno && canEditConsuntivo && !isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null) && handleCellClick(orario.id, 'oraConsInizio', orario.oraConsInizio)}
                            sx={{ 
                              cursor: (canEditOrario(orario.data) && canModifyThisTurno && canEditConsuntivo && !isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null)) ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              height: '32px',
                              color: orario.oraConsInizio ? 'inherit' : '#999',
                              opacity: (canEditOrario(orario.data) && canModifyThisTurno && canEditConsuntivo) ? 1 : 0.6
                            }}
                          >
                            {orario.codiceTipoOrario !== 'CHIUSO DITTA' && <AccessTime sx={{ fontSize: 16, color: '#999' }} />}
                            {orario.codiceTipoOrario === 'CHIUSO DITTA' ? '' : (isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null) ? '—' : (orario.oraConsInizio ? formatTime(orario.oraConsInizio) : '--:--'))}
                          </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        backgroundColor: getCellBackgroundColor(orario.oraConsFine, orario.oraPrevFine),
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)'
                      }}>
                        {isEditing ? (
                          (isDisableConsuntivo(isEditing.tipoOrario) && !((isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && isEditing.tipoOrario === 'ROL')) ? (
                            <Typography variant="body2" color="text.disabled">
                              {isEditing.tipoOrario === 'CHIUSO DITTA' ? '' : '—'}
                            </Typography>
                          ) : (
                            <TextField
                              type="time"
                              value={isEditing.oraConsFine}
                              onChange={(e) => updateEditingValue(orario.id, 'oraConsFine', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, orario.id, 'oraConsFine')}
                              size="small"
                              disabled={!canModifyThisTurno || !canEditConsuntivo || (isDisableConsuntivo(isEditing.tipoOrario) && !((isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && isEditing.tipoOrario === 'ROL')) || (isAssenzaConConsuntivo(isEditing.tipoOrario) && !((isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && isEditing.tipoOrario === 'ROL') && orari.some(o => o.data === orario.data && o.id !== orario.id && (o.tipoOrario === 'LAVORATO' || o.codiceTipoOrario === 'LAVORATO')))}
                              sx={{ 
                                width: '100%',
                                '& .MuiInputBase-input': { padding: '4px' }
                              }}
                              inputProps={{ 
                                step: 300,
                                'data-field': 'oraConsFine',
                                'data-row': orario.id
                              }}
                              autoFocus={activeCell?.rowId === orario.id && activeCell?.field === 'oraConsFine'}
                            />
                          )
                        ) : (
                          <Tooltip title={hasPreviousUnconfirmed ? 'Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)' : ''}>
                          <Box 
                            className="time-cell"
                            onClick={() => {
                              const isRigaDuplicataRolAdmin = (isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && (orario.codiceTipoOrario === 'ROL' || orario.tipoOrario === 'ROL');
                              const canClick = canModifyThisTurno && canEditConsuntivo && (!isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null) || isRigaDuplicataRolAdmin);
                              if (canClick) handleCellClick(orario.id, 'oraConsFine', orario.oraConsFine);
                            }}
                            sx={{ 
                              cursor: (() => {
                                const isRigaDuplicataRolAdmin = (isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && (orario.codiceTipoOrario === 'ROL' || orario.tipoOrario === 'ROL');
                                return (canEditOrario(orario.data) && canModifyThisTurno && canEditConsuntivo && (!isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null) || isRigaDuplicataRolAdmin)) ? 'pointer' : 'not-allowed';
                              })(),
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              height: '32px',
                              color: orario.oraConsFine ? 'inherit' : '#999',
                              opacity: (canEditOrario(orario.data) && canModifyThisTurno && canEditConsuntivo) ? 1 : 0.6
                            }}
                          >
                            {orario.codiceTipoOrario !== 'CHIUSO DITTA' && <AccessTime sx={{ fontSize: 16, color: '#999' }} />}
                            {orario.codiceTipoOrario === 'CHIUSO DITTA' ? '' : ((isDisableConsuntivo(orario.codiceTipoOrario || orario.tipoOrario || null) && !((isAdminViewer || isCoordinatoreViewer) && orario.annotazioni === '__RIGA_DUPLICATA__' && (orario.codiceTipoOrario === 'ROL' || orario.tipoOrario === 'ROL'))) ? '—' : (orario.oraConsFine ? formatTime(orario.oraConsFine) : '--:--'))}
                          </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className="tipo-orario-cell">
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ width: '100%' }}>
                            <Select
                              value={isEditing.tipoOrario}
                              onChange={(e) => updateEditingValue(orario.id, 'tipoOrario', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, orario.id, 'tipoOrario')}
                              autoFocus={activeCell?.rowId === orario.id && activeCell?.field === 'tipoOrario'}
                              displayEmpty
                              disabled={!canModifyThisTurno}
                              sx={{ 
                                '& .MuiSelect-select': { padding: '4px 24px 4px 8px' }
                              }}
                            >
                              <MenuItem value="">
                                <em>Seleziona tipo orario</em>
                              </MenuItem>
                              {(() => {
                                const role = (userInfo.ruolo || '').toLowerCase();
                                const isDipendente = role === 'dipendente';
                                let optionsToRender = TIPO_ORARIO_OPTIONS_FILTERED;

                                if (isDipendente) {
                                  const current = isEditing.tipoOrario;
                                  // Base ristrette per dipendente
                                  let baseRestricted = [...TIPO_ORARIO_OPTIONS_RESTRICTED];
                                  // Se l'utente ha permessi speciali (Tipo=1) e ha ancora giorni disponibili, permetti LEGGE 104 PERMESSO
                                  // MA solo se è Admin (altrimenti non può mai accedere a LEGGE 104 PERMESSO)
                                  if (isAdminViewer && permessiSpeciali && permessiSpeciali.hasSpecial && permessiSpeciali.tipo === 1 && (permessiSpeciali.remainingGg ?? 0) > 0) {
                                    if (!baseRestricted.includes('LEGGE 104 PERMESSO')) baseRestricted.push('LEGGE 104 PERMESSO');
                                  }
                                  // Se il valore corrente non è nelle opzioni ristrette (es. era un vecchio valore), lo aggiungiamo temporaneamente
                                  // Altrimenti mostriamo solo le opzioni ristrette
                                  optionsToRender = (current && !baseRestricted.includes(current))
                                    ? [current, ...baseRestricted]
                                    : baseRestricted;
                                }

                                // Rimuovi duplicati
                                let uniqueOptions = Array.from(new Set(optionsToRender));

                                const isHoliday = !!festivitaData?.some((f: any) => f.dataFestivita === orario.data);
                                const allowedHolidayTypes = new Set(['LAVORATO', 'MALATTIA', "MATERNITA'", 'CHIUSO DITTA', 'LEGGE 104 PERMESSO']);

                                // Su festività mantieni un eventuale valore legacy per correggerlo,
                                // ma non permettere nuove conversioni verso tipi non consentiti.
                                if (isHoliday) {
                                  uniqueOptions = uniqueOptions.filter(opt => opt === isEditing.tipoOrario || allowedHolidayTypes.has(opt));
                                }

                                // FILTRO CHIUSO DITTA: Mostra solo se la data è presente in festivita
                                uniqueOptions = uniqueOptions.filter(opt => {
                                  if (opt === 'CHIUSO DITTA') {
                                    return isHoliday;
                                  }
                                  return true;
                                });

                                // FILTRO ROL: Se ore contrattuali = 0, nascondi ROL
                                if (dipendenteInfo) {
                                  const d = dayjs(orario.data);
                                  const dayOfWeek = d.day(); // 0=Dom, 1=Lun...
                                  let hours = 0;
                                  switch (dayOfWeek) {
                                    case 1: hours = dipendenteInfo.lunedi; break;
                                    case 2: hours = dipendenteInfo.martedi; break;
                                    case 3: hours = dipendenteInfo.mercoledi; break;
                                    case 4: hours = dipendenteInfo.giovedi; break;
                                    case 5: hours = dipendenteInfo.venerdi; break;
                                    case 6: hours = dipendenteInfo.sabato; break;
                                    case 0: hours = dipendenteInfo.domenica; break;
                                  }
                                  if (hours <= 0) {
                                    uniqueOptions = uniqueOptions.filter(o => o !== 'ROL');
                                  }
                                }

                                return uniqueOptions.map((option) => {
                                  // LEGGE 104: mostra grigio con tooltip quando limite raggiunto (per Admin/Coord/abilitaInsOrari)
                                  if (option === 'LEGGE 104 PERMESSO' && !isAdminViewer && viewedCodicePersonale) {
                                    const info104 = utilizzo104[viewedCodicePersonale];
                                    if (info104 && info104.remainingGg === 0) {
                                      return (
                                        <Tooltip
                                          key={option}
                                          title={<span style={{ color: '#f44336' }}>Limite permessi raggiunto ({info104.usedGg}/{info104.ggMese} gg)</span>}
                                          placement="right"
                                        >
                                          <span>
                                            <MenuItem value={option} disabled sx={{ color: '#aaa', fontStyle: 'italic' }}>
                                              {option}
                                            </MenuItem>
                                          </span>
                                        </Tooltip>
                                      );
                                    }
                                  }
                                  return (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  );
                                });
                              })()}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box 
                            onClick={() => canModifyThisTurno && handleCellClick(orario.id, 'tipoOrario', orario.tipoOrario || null)}
                            sx={{ 
                              cursor: (canEditOrario(orario.data) && canModifyThisTurno) ? 'pointer' : 'not-allowed',
                              padding: '8px',
                              minHeight: '24px',
                              color: orario.tipoOrario ? 'inherit' : '#999',
                              backgroundColor: !orario.tipoOrario && (orario.oraConsInizio || orario.oraConsFine) ? '#fffbf0' : 'inherit', // giallino molto chiaro
                              opacity: (canEditOrario(orario.data) && canModifyThisTurno) ? 1 : 0.6
                            }}
                          >
                            {(() => {
                              const isAssenzaTurno = (orario.turno || '').toUpperCase() === 'ASSENZA';
                              const showMask = shouldMaskAssenze && isActingForAnother && isAssenzaTurno;
                              if (showMask) return 'ASSENZA';
                              return orario.tipoOrario || ((orario.oraConsInizio || orario.oraConsFine) ? 'Aggiungi Il tipo orario' : 'Non specificato');
                            })()}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell className="annotazioni-cell">
                        {isEditing ? (
                          <TextField
                            value={isEditing.annotazioni}
                            onChange={(e) => updateEditingValue(orario.id, 'annotazioni', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, orario.id, 'annotazioni')}
                            size="small"
                            disabled={!canModifyThisTurno}
                            placeholder="Inserisci annotazioni"
                            autoFocus={activeCell?.rowId === orario.id && activeCell?.field === 'annotazioni'}
                          />
                        ) : (
                          <Box 
                            onClick={() => canModifyThisTurno && handleCellClick(orario.id, 'annotazioni', orario.annotazioni === '__RIGA_DUPLICATA__' ? null : orario.annotazioni || null)}
                            sx={{ 
                              cursor: (canEditOrario(orario.data) && canModifyThisTurno) ? 'pointer' : 'not-allowed',
                              padding: '8px',
                              minHeight: '24px',
                              color: (orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__') ? 'inherit' : '#999',
                              opacity: (canEditOrario(orario.data) && canModifyThisTurno) ? 1 : 0.6
                            }}
                          >
                            {(orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__') ? orario.annotazioni : '--'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {orario.annotazioni === '__RIGA_DUPLICATA__' ? (
                          // Righe aggiunte dall'utente: solo pulsante cancella
                          <Tooltip title={canModifyThisTurno ? "Cancella riga aggiunta" : "Non puoi eliminare questo turno"}>
                            <span>
                            <IconButton 
                              onClick={() => deleteRow(orario)}
                              size="small"
                              disabled={!canModifyThisTurno}
                              sx={{ color: canModifyThisTurno ? '#f44336' : '#ccc' }}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          // Riga originale (sia dal database che generata automaticamente): solo pulsante duplica
                          <Tooltip title={(canEditOrario(orario.data) && canModifyThisTurno) ? "Duplica riga" : (canModifyThisTurno ? "Modifica non consentita per questa data" : "Non puoi modificare questo turno")}>
                            <span>
                            <IconButton 
                              onClick={() => duplicateRow(orario)}
                              size="small"
                              disabled={!canEditOrario(orario.data) || !canModifyThisTurno}
                              sx={{ color: (canEditOrario(orario.data) && canModifyThisTurno) ? '#2196F3' : '#ccc' }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {showActions ? (
                          // Durante l'editing: mostra pulsanti Salva e X
                          <Box className="editing-buttons" sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Salva">
                              <IconButton 
                                onClick={() => saveConsuntivo(orario.id)}
                                size="small"
                                sx={{ color: 'green' }}
                              >
                                <Save fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canModifyThisTurno ? (
                              <Tooltip title={orario.oraConsInizio || orario.oraConsFine || orario.tipoOrario || (orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__') ? "Cancella il turno" : "Annulla modifica"}>
                                <IconButton 
                                  onClick={() => handleCancelOrClear(orario.id, orario)}
                                  size="small"
                                  sx={{ color: 'red' }}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Annulla modifica">
                                <IconButton 
                                  onClick={() => {
                                    setEditingRows(prev => {
                                      const newState = { ...prev };
                                      delete newState[orario.id];
                                      return newState;
                                    });
                                    setShowActionsForRow(null);
                                    setActiveCell(null);
                                  }}
                                  size="small"
                                  sx={{ color: 'grey' }}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          // Fuori dall'editing: mostra solo il pulsante X se ci sono dati consuntivi E il turno è modificabile
                          (orario.oraConsInizio || orario.oraConsFine) && canModifyThisTurno && (
                            <Tooltip title="Cancella il turno">
                              <IconButton 
                                onClick={() => handleCancelOrClear(orario.id, orario)}
                                size="small"
                                sx={{ color: 'red' }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f5f5f5' }}>
                        {orario.turno?.toUpperCase?.() === 'RIPOSO' ? '-' : calculateTotalHours(orario.oraConsInizio, orario.oraConsFine)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Riga di totale */}
                <TableRow>
                  <TableCell colSpan={12} sx={{ fontWeight: 'bold', textAlign: 'right', backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                    Totale:
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                    {totalOreSum}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {orari.length === 0 && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Nessun orario trovato per il periodo selezionato
              </Typography>
            </Box>
          )}
        </Paper>
        </Box> 
      </Box>
      
      {/* Dialog per selezione turno */}
      <Dialog open={turnoDialogOpen} onClose={() => { if (!preferenceSubmitting) setTurnoDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {(dialogType === 'insert' || dialogType === 'updateRiposo') && 'Seleziona tipo di turno per la preferenza'}
          {dialogType === 'modify' && `Modifica preferenza: ${selectedOrario?.turno}`}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const isW = selectedOrario ? isWeekend(selectedOrario.data) : false;
            // Intermezzo commentato per ora - non più utilizzato
            // const options = ['Mattina', 'Pomeriggio', 'Intermezzo', 'Riposo']
            const options = ['Mattina', 'Pomeriggio', 'Riposo']
              .filter(t => {
                // Se è weekend (sabato/domenica), nascondi "Riposo" - mostro solo Mattina e Pomeriggio
                if (selectedOrario && isW && t === 'Riposo') return false;
                if (selectedOrario && festivitaData?.some((f: any) => f.dataFestivita === selectedOrario.data) && t === 'Riposo') return false;
                return true;
              });
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                {options.map((turno) => (
                  <Button
                    key={turno}
                    variant="contained"
                    fullWidth
                    onClick={() => handleTurnoSelection(turno)}
                    disabled={preferenceSubmitting}
                    sx={{
                      // Riposo centrato sotto Mattina e Pomeriggio nei giorni feriali (lun-ven)
                      ...(!isW && turno === 'Riposo' ? { gridColumn: '1 / -1', maxWidth: '50%', justifySelf: 'center' } : {}),
                      backgroundColor: getTurnoColor(turno),
                      color: getTurnoTextColor(turno),
                      borderRadius: 3,
                      py: 2,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: getTurnoColor(turno),
                        opacity: 0.8
                      }
                    }}
                  >
                    {turno}
                  </Button>
                ))}
              </Box>
            );
          })()}
          
          {dialogType === 'modify' && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeletePreference}
                disabled={preferenceSubmitting}
                sx={{ borderRadius: 3 }}
              >
                Cancella Preferenza
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTurnoDialogOpen(false)} disabled={preferenceSubmitting}>Annulla</Button>
        </DialogActions>
      </Dialog>
      
      {/* Popup per la gestione dei cambi turno */}
      {showCambiTurno && (
        <CambiTurnoPopup
          open={cambiTurnoPopupOpen}
          onClose={() => setCambiTurnoPopupOpen(false)}
          turniDipendente={orari as any} // Cast temporaneo per compatibilità tipi
          user={{
            nome: userInfo.nome || '',
            ruolo: userInfo.ruolo || 'Dipendente',
            negozio: userInfo.negozio || ''
          }}
          onRefresh={loadCambiTurnoStats}
        />
      )}

      {/* Dialog limite preferenze settimanali */}
      <PreferenceLimitDialog
        open={limitDialogOpen}
        preferences={limitDialogWeekPrefs}
	        onDelete={async (id) => {
	          try {
	            await deletePreference(id);
		            await refreshAfterTurnoWrite();
	            // Aggiorna lista nel dialog
	            const remaining = limitDialogWeekPrefs.filter(p => p.id !== id);
	            setLimitDialogWeekPrefs(remaining);
	            // Chiudi se ne resta <=1
	            if (remaining.length <= 1) setLimitDialogOpen(false);
	          } catch (e) {
            console.error('Errore cancellazione preferenza da dialog:', e);
          }
        }}
        onClose={() => setLimitDialogOpen(false)}
      />

      {/* Sezione Statistiche Dipendente */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Schedule sx={{ mr: 1, color: PAGE_COLOR }} />
          <Typography variant="h6">Statistiche Dipendente - {selectedDate.format('MMMM YYYY')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Statistiche compatte a sinistra */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'stretch', flex: 1 }}>
            {[
              { label: 'Ore contratto', value: stats.oreContratto },
              { label: 'Ore pianificate', value: stats.orePianificate },
              { label: 'Ore consuntivate', value: stats.oreConsuntivate },
              { label: 'Assenze contrattuali', value: stats.assenzeContrattuali },
              { label: 'Attuale', value: stats.attuale, color: stats.attuale >= stats.oreContratto ? 'success.main' : 'error.main' },
              { label: 'Differenza Attuale', value: stats.differenzaAttuale, color: stats.differenzaAttuale >= 0 ? 'success.main' : 'error.main' },
            ].map((it) => (
              <Box key={it.label} sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 1,
                minWidth: 150,
                flex: '0 0 auto',
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="caption" color="text.secondary">{it.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: it.color || 'inherit' }}>
                  {formatHoursNumberToHHSS(it.value)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Sezione a destra: Tipo Contratto + ore settimanali */}
          <Box sx={{
            minWidth: 280,
            borderLeft: '1px dashed #ddd',
            pl: 2
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Contratto</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.5, columnGap: 1 }}>
              <Typography variant="caption" color="text.secondary">Tipo Contratto</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {(() => {
                  const df = dipendenteInfo?.dataFineContratto?.slice(0,10);
                  // Considera INDETERMINATO anche quando la data fine non è valorizzata
                  return !df || df === '2099-12-31' ? 'INDETERMINATO' : 'DETERMINATO';
                })()}
              </Typography>
              {/* Giornaliero */}
              <Typography variant="caption" color="text.secondary">Lun</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.lunedi || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Mar</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.martedi || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Mer</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.mercoledi || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Gio</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.giovedi || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Ven</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.venerdi || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Sab</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.sabato || 0)}</Typography>
              <Typography variant="caption" color="text.secondary">Dom</Typography>
              <Typography variant="body2">{formatHoursNumberToHHSS(dipendenteInfo?.domenica || 0)}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
      
    </Container>
  );
};

// Converte un numero di ore (decimali) nel formato UI hh:ss
const formatHoursNumberToHHSS = (value: number): string => {
  if (value === null || value === undefined || isNaN(value as any)) return '0:00';
  const sign = value < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(value) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

export default MioOrario;
